import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from '@/utils/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { 
  Building2, Lock, Users, UserCheck, UserX, Upload, Plus, Send, 
  LogOut, RefreshCw, Download, Search, X, Loader2,
  Megaphone, Bell, Settings, Image as ImageIcon, Video, Trash2,
  ToggleLeft, ToggleRight, Calendar, Link as LinkIcon, Link, Sparkles,
  Mic, Brain, FileText, Palette, BarChart3, Shield, Crown, Flame, Award, Smile, CheckCircle, XCircle,
  Moon, Trophy, Eye, MousePointer, Clock, Edit2, Check
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import Footer from '@/components/layout/Footer';
import VideoEmbedsManagerUnified from '@/components/VideoEmbedsManagerUnified';
import B2BBannerManager from '@/components/b2b/B2BBannerManager';
import B2BPromoCampaignManager from '@/components/b2b/B2BPromoCampaignManager';

const API_BASE = '';

// ==================== COLOR CONTRAST UTILITIES ====================
// Determines if a color is light or dark, useful for choosing text colors
const getLuminance = (hex) => {
  // Remove # if present
  const color = hex.replace('#', '');
  const r = parseInt(color.substr(0, 2), 16) / 255;
  const g = parseInt(color.substr(2, 2), 16) / 255;
  const b = parseInt(color.substr(4, 2), 16) / 255;
  
  // Calculate relative luminance
  const toLinear = (c) => c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
};

const isLightColor = (hex) => {
  if (!hex) return false;
  return getLuminance(hex) > 0.5;
};

const isDarkColor = (hex) => {
  if (!hex) return true;
  return getLuminance(hex) <= 0.3;
};

// Get contrasting text color for a background
const getContrastTextColor = (bgColor, lightText = '#FFFFFF', darkText = '#1a1a1a') => {
  if (!bgColor) return lightText;
  return isLightColor(bgColor) ? darkText : lightText;
};

// Get a readable muted text color based on background
const getMutedTextColor = (bgColor, textColor) => {
  if (!bgColor || !textColor) return textColor;
  // If background is dark, return a lighter muted version
  if (isDarkColor(bgColor)) {
    return '#94a3b8'; // Slate-400 - visible on dark backgrounds
  }
  // If background is light, return a darker muted version
  return '#64748b'; // Slate-500 - visible on light backgrounds
};

// Get a readable section header color based on background
// Uses the primary text color for maximum readability
const getSectionHeaderColor = (bgColor, textPrimary, accentColor) => {
  // Always prefer text_primary for section headers as it's designed for readability
  // Only use accent if it has good contrast
  if (!bgColor || !accentColor) return textPrimary || '#F8FAFC';
  
  // Check if accent color has sufficient contrast with background
  const bgLum = getLuminance(bgColor);
  const accentLum = getLuminance(accentColor);
  const contrastRatio = (Math.max(bgLum, accentLum) + 0.05) / (Math.min(bgLum, accentLum) + 0.05);
  
  // If contrast ratio is less than 4.5:1 (WCAG AA), use text_primary instead
  if (contrastRatio < 4.5) {
    return textPrimary || '#F8FAFC';
  }
  
  return accentColor;
};

// Neutral theme for login screen (no UnCloned branding)
// Once logged in, client's actual theme will be applied
const DEFAULT_THEME = {
  primary_color: "#3B82F6",        // Neutral blue
  secondary_color: "#6366F1",      // Indigo
  accent_color: "#22D3EE",         // Cyan
  background_primary: "#0F172A",   // Slate-900
  background_secondary: "#1E293B", // Slate-800
  background_tertiary: "#334155",  // Slate-700
  text_primary: "#F8FAFC",         // Slate-50
  text_secondary: "#CBD5E1",       // Slate-300
  border_color: "#475569",         // Slate-600
  success_color: "#22c55e",        // Green
  warning_color: "#f59e0b",        // Amber
  error_color: "#ef4444",          // Red
  mode: "dark"
};

// Merge client theme with defaults
// If NEW 4-color fields exist, use ONLY those (ignore legacy fields)
// This prevents old colors from bleeding through
const mergeThemeWithDefaults = (clientTheme) => {
  if (!clientTheme) return { ...DEFAULT_THEME };
  
  // Check if client has the new color fields (either 4-color or 8-color system)
  const hasNewFields = clientTheme.brand_color || clientTheme.background_color || 
                       clientTheme.card_color || clientTheme.text_color;
  
  if (hasNewFields) {
    // Core 4 colors
    const brand = clientTheme.brand_color || DEFAULT_THEME.primary_color;
    const background = clientTheme.background_color || DEFAULT_THEME.background_primary;
    const card = clientTheme.card_color || DEFAULT_THEME.background_secondary;
    const text = clientTheme.text_color || DEFAULT_THEME.text_primary;
    
    // Additional 4 colors - use stored values if available, otherwise derive smart defaults
    const border = clientTheme.border_color || card;
    const accent = clientTheme.accent_color || brand;
    const secondaryText = clientTheme.secondary_text_color || getMutedTextColor(background, text);
    const inputBg = clientTheme.input_background || card;
    
    // For background_tertiary: use input_background if set, otherwise use card
    const bgTertiary = clientTheme.input_background || clientTheme.background_tertiary || card;
    
    // Determine if brand/accent colors need dark text for buttons
    const brandNeedsDarkText = isLightColor(brand);
    const accentNeedsDarkText = isLightColor(accent);
    const cardNeedsDarkText = isLightColor(card);
    
    return {
      // New 8-color fields
      brand_color: brand,
      background_color: background,
      card_color: card,
      text_color: text,
      border_color: border,
      accent_color: accent,
      secondary_text_color: secondaryText,
      input_background: inputBg,
      // Map to legacy field names for compatibility
      primary_color: brand,
      secondary_color: accent,
      background_primary: background,
      background_secondary: card,
      background_tertiary: bgTertiary,
      text_primary: text,
      text_secondary: secondaryText,
      // Contrast-aware text colors for buttons/cards
      text_on_brand: brandNeedsDarkText ? '#1a1a1a' : '#ffffff',
      text_on_accent: accentNeedsDarkText ? '#1a1a1a' : '#ffffff',
      text_on_card: cardNeedsDarkText ? '#1a1a1a' : text,
      // Derive semantic colors from brand
      success_color: brand,
      warning_color: accent,  
      error_color: "#ef4444",
      mode: "flat"
    };
  }
  
  // Fallback: merge all fields (for legacy clients that haven't saved yet)
  // But still derive semantic colors from primary to avoid UnCloned branding
  const merged = { ...DEFAULT_THEME };
  Object.keys(clientTheme).forEach(key => {
    const value = clientTheme[key];
    if (value !== null && value !== undefined && value !== '') {
      merged[key] = value;
    }
  });
  
  // Override semantic colors with primary to maintain brand consistency
  const primaryBrand = merged.primary_color || DEFAULT_THEME.primary_color;
  const cardColor = merged.card_color || merged.background_secondary || DEFAULT_THEME.card_color;
  const bgColor = merged.background_color || merged.background_primary || DEFAULT_THEME.background_primary;
  
  merged.success_color = primaryBrand;
  merged.warning_color = primaryBrand;
  merged.accent_color = primaryBrand;
  merged.border_color = cardColor;
  merged.text_secondary = getMutedTextColor(bgColor, merged.text_primary);
  merged.text_on_brand = isLightColor(primaryBrand) ? '#1a1a1a' : '#ffffff';
  merged.text_on_card = isLightColor(cardColor) ? '#1a1a1a' : merged.text_primary;
  merged.mode = "flat";
  
  return merged;
};

// Get CSS variable style object from theme
const getThemeStyles = (theme) => ({
  '--theme-primary': theme.primary_color || DEFAULT_THEME.primary_color,
  '--theme-secondary': theme.secondary_color || DEFAULT_THEME.secondary_color,
  '--theme-accent': theme.accent_color || DEFAULT_THEME.accent_color,
  '--theme-bg-primary': theme.background_primary || DEFAULT_THEME.background_primary,
  '--theme-bg-secondary': theme.background_secondary || DEFAULT_THEME.background_secondary,
  '--theme-bg-tertiary': theme.background_tertiary || DEFAULT_THEME.background_tertiary,
  '--theme-text-primary': theme.text_primary || DEFAULT_THEME.text_primary,
  '--theme-text-secondary': theme.text_secondary || DEFAULT_THEME.text_secondary,
  '--theme-border': theme.border_color || DEFAULT_THEME.border_color,
  '--theme-success': theme.success_color || DEFAULT_THEME.success_color,
  '--theme-warning': theme.warning_color || DEFAULT_THEME.warning_color,
  '--theme-error': theme.error_color || DEFAULT_THEME.error_color,
});

export default function ClientPortal() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [theme, setTheme] = useState(DEFAULT_THEME);
  const [originalAdminTheme, setOriginalAdminTheme] = useState(null); // Store admin-set theme for reset
  
  // Preview mode state
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [previewClient, setPreviewClient] = useState(null);
  
  // Login state
  const [clientId, setClientId] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  
  // Invite state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteFirstName, setInviteFirstName] = useState('');
  const [inviteLastName, setInviteLastName] = useState('');
  
  // Bulk upload state
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkUsers, setBulkUsers] = useState([]);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Admin tab state
  const [activeTab, setActiveTab] = useState('users'); // 'users', 'promos', 'notifications'
  const [hasAdminAccess, setHasAdminAccess] = useState(true); // Always show tabs, content will be empty if no access
  const [showUpgradeModal, setShowUpgradeModal] = useState(false); // For locked feature popup
  
  // User detail modal state
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDetailModal, setShowUserDetailModal] = useState(false);
  const [userStats, setUserStats] = useState(null);
  const [loadingUserStats, setLoadingUserStats] = useState(false);
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [editUserForm, setEditUserForm] = useState({ first_name: '', last_name: '' });
  const [savingUserEdit, setSavingUserEdit] = useState(false);
  
  // User credentials dropdown state
  const [showUserCredentialsMenu, setShowUserCredentialsMenu] = useState(null);
  const [sendingUserCredentials, setSendingUserCredentials] = useState(null);
  
  // Promo campaigns state
  const [promos, setPromos] = useState([]);
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [promoForm, setPromoForm] = useState({
    banner_type: 'styled',
    title: '',
    subtitle: '',
    description: '',
    cta_text: 'Learn More',
    cta_link: '',
    discount_label: '',
    gradient_from: '',  // Will use theme.primary_color
    gradient_to: '',    // Will use theme.secondary_color
    image_url: '',
    video_url: '',
    is_active: false,
    priority: 0
  });
  
  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const notificationsFetchVersion = React.useRef(0); // Track fetch versions to prevent stale updates
  const [showNotifModal, setShowNotifModal] = useState(false);
  const [editingNotif, setEditingNotif] = useState(null);
  const [notifForm, setNotifForm] = useState({
    title: '',
    message: '',
    type: 'announcement',
    is_active: true,
    dismissible: true,
    show_popup: true,
    image_url: '',
    action_buttons: [],
    target_type: 'all',
    target_user_ids: [],
    target_filter: '',
    target_user_search: '',
    scheduled_at: '',
    expires_at: '',
    use_personalization: true
  });
  const [notifImagePreview, setNotifImagePreview] = useState(null);
  const [clientUsers, setClientUsers] = useState([]);
  const [notificationAnalytics, setNotificationAnalytics] = useState(null);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [notificationDetails, setNotificationDetails] = useState(null);
  
  // Upload state
  const [uploading, setUploading] = useState(false);

  // Detailed Analytics state
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  // Banners state
  const [banners, setBanners] = useState([]);
  const [showBannerModal, setShowBannerModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [bannerForm, setBannerForm] = useState({
    title: '',
    subtitle: '',
    cta_text: 'Learn More',
    cta_link: '',
    image_url: '',
    gradient_from: '',  // Will use theme.primary_color
    gradient_to: '',    // Will use theme.secondary_color
    position: 'main',
    is_active: false,
    priority: 0
  });

  // Branding/Theme state - EXPANDED 8-COLOR SYSTEM
  const [brandingForm, setBrandingForm] = useState({
    brand_color: '#3B82F6',
    background_color: '#0F172A',
    card_color: '#1E293B',
    text_color: '#F8FAFC',
    border_color: '#475569',
    accent_color: '#22D3EE',
    secondary_text_color: '#94A3B8',
    input_background: '#1E293B'
  });
  const [savingBranding, setSavingBranding] = useState(false);

  useEffect(() => {
    // Check for preview mode
    const previewClientId = searchParams.get('preview');
    if (previewClientId) {
      loadPreviewMode(previewClientId);
    } else {
      // Check if already authenticated via cookie (auto-login)
      checkAuthStatus();
    }
  }, [searchParams]);
  
  // Apply theme CSS variables to document root when theme changes
  useEffect(() => {
    if (!isAuthenticated) return; // Don't apply theme on login page
    
    const root = document.documentElement;
    const body = document.body;
    
    // Apply all theme colors as CSS variables to :root
    root.style.setProperty('--theme-primary', theme.primary_color || DEFAULT_THEME.primary_color);
    root.style.setProperty('--theme-secondary', theme.secondary_color || DEFAULT_THEME.secondary_color);
    root.style.setProperty('--theme-background', theme.background_primary || DEFAULT_THEME.background_primary);
    root.style.setProperty('--theme-background-secondary', theme.background_secondary || DEFAULT_THEME.background_secondary);
    root.style.setProperty('--theme-text', theme.text_primary || DEFAULT_THEME.text_primary);
    root.style.setProperty('--theme-text-secondary', theme.text_secondary || DEFAULT_THEME.text_secondary);
    root.style.setProperty('--theme-border', theme.border_color || DEFAULT_THEME.border_color);
    
    // Set the page background - SOLID color, NO GRADIENTS for B2B
    const bgPrimary = theme.background_primary || DEFAULT_THEME.background_primary;
    
    root.style.setProperty('--theme-page-bg', bgPrimary);
    
    // Also apply directly to body to ensure it overrides any CSS defaults
    body.style.background = bgPrimary;
    
    // Set RGB versions for transparency effects
    const hexToRgb = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : null;
    };
    
    const bgRgb = hexToRgb(bgPrimary);
    const bgSecRgb = hexToRgb(theme.background_secondary || DEFAULT_THEME.background_secondary);
    const primaryRgb = hexToRgb(theme.primary_color || DEFAULT_THEME.primary_color);
    const borderRgb = hexToRgb(theme.border_color || DEFAULT_THEME.border_color);
    
    if (bgRgb) root.style.setProperty('--theme-background-rgb', bgRgb);
    if (bgSecRgb) root.style.setProperty('--theme-background-secondary-rgb', bgSecRgb);
    if (primaryRgb) root.style.setProperty('--theme-primary-rgb', primaryRgb);
    if (borderRgb) root.style.setProperty('--theme-border-rgb', borderRgb);
    
    // Convert hex to HSL for Tailwind's ring variable
    const hexToHsl = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      if (!result) return null;
      let r = parseInt(result[1], 16) / 255;
      let g = parseInt(result[2], 16) / 255;
      let b = parseInt(result[3], 16) / 255;
      const max = Math.max(r, g, b), min = Math.min(r, g, b);
      let h, s, l = (max + min) / 2;
      if (max === min) {
        h = s = 0;
      } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
          case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
          case g: h = ((b - r) / d + 2) / 6; break;
          case b: h = ((r - g) / d + 4) / 6; break;
          default: h = 0;
        }
      }
      return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
    };
    
    // Override Tailwind's ring color variable to match B2B theme
    const primaryHsl = hexToHsl(theme.primary_color || DEFAULT_THEME.primary_color);
    const borderHsl = hexToHsl(theme.border_color || DEFAULT_THEME.border_color);
    if (primaryHsl) root.style.setProperty('--ring', primaryHsl);
    if (borderHsl) root.style.setProperty('--border', borderHsl);
    
    // Cleanup function to reset body style when leaving client portal
    return () => {
      body.style.background = '';
    };
  }, [theme, isAuthenticated]);
  
  const loadPreviewMode = async (clientId) => {
    setLoading(true);
    try {
      // Fetch client data for preview (uses public endpoint)
      const res = await axios.get(`${API_BASE}/api/client-portal/preview/${clientId}`);
      setPreviewClient(res.data.client);
      setIsPreviewMode(true);
      setIsAuthenticated(true); // Fake auth for UI display
      
      // Get features from client data
      const features = res.data.client?.features || {};
      
      // In preview mode, show admin tabs if client_admin_portal is enabled
      // This lets admins see exactly what the B2B client will see
      if (features.client_admin_portal !== false) {
        setHasAdminAccess(true);
      }
      
      // Create mock dashboard data for preview
      setDashboard({
        client: res.data.client,
        users: res.data.sample_users || [],
        stats: res.data.stats || {
          total_licenses: res.data.client.seats || 50,
          activated: 0,
          pending: 0
        }
      });
      
      // Apply theme
      if (res.data.client?.theme) {
        setTheme(mergeThemeWithDefaults(res.data.client.theme));
      }
      
      // Load admin data if available (promos, notifications, banners)
      if (res.data.promos) setPromos(res.data.promos);
      if (res.data.notifications) setNotifications(res.data.notifications);
      if (res.data.banners) setBanners(res.data.banners);
      
    } catch (err) {
      console.error('Failed to load preview:', err);
      toast.error('Failed to load preview');
      setIsPreviewMode(false);
    } finally {
      setLoading(false);
    }
  };

  const checkAuthStatus = async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/client-portal/dashboard`, {
        withCredentials: true
      });
      if (res.data) {
        setIsAuthenticated(true);
        setDashboard(res.data);
        // Apply client theme from dashboard
        if (res.data.client?.theme) {
          setTheme(mergeThemeWithDefaults(res.data.client.theme));
        }
        // Check if client has admin access
        const features = res.data.client?.features || {};
        if (features.client_admin_portal) {
          fetchAdminData();
        }
      }
    } catch (err) {
      // Not authenticated, show login form
      setIsAuthenticated(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoading(true);
    
    try {
      const res = await axios.post(`${API_BASE}/api/client-portal/login`, {
        client_id: clientId,
        password: password
      }, { withCredentials: true });
      
      // Store only non-sensitive UI preferences
      sessionStorage.setItem('clientPortalName', res.data.client.name);
      
      // Apply client theme if available
      if (res.data.client.theme) {
        setTheme(mergeThemeWithDefaults(res.data.client.theme));
      }
      
      setIsAuthenticated(true);
      fetchDashboard();
      toast.success(`Welcome, ${res.data.client.name}!`);
    } catch (err) {
      setLoginError(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/api/client-portal/dashboard`, {
        withCredentials: true
      });
      setDashboard(res.data);
      
      // Populate client users from dashboard for targeting
      if (res.data.users) {
        setClientUsers(res.data.users);
      }
      
      // Apply client theme from dashboard
      if (res.data.client?.theme) {
        setTheme(mergeThemeWithDefaults(res.data.client.theme));
      }
      
      // Store the admin default theme for reset functionality
      if (res.data.client?.admin_default_theme) {
        setOriginalAdminTheme({ ...res.data.client.admin_default_theme });
      } else if (res.data.client?.theme && !originalAdminTheme) {
        // Fallback: use current theme if admin_default_theme not set yet
        setOriginalAdminTheme({ ...res.data.client.theme });
      }
      
      // Check if client has admin access
      const features = res.data.client?.features || {};
      if (features.client_admin_portal) {
        setHasAdminAccess(true);
        // Fetch promos and notifications
        fetchAdminData();
      }
    } catch (err) {
      if (err.response?.status === 401) {
        handleLogout();
      }
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminData = async (forceUpdate = false) => {
    // Increment version before fetching
    const currentVersion = ++notificationsFetchVersion.current;
    
    try {
      const [promosRes, notifsRes] = await Promise.all([
        axios.get(`${API_BASE}/api/client-admin/promos`, {
          withCredentials: true
        }),
        axios.get(`${API_BASE}/api/client-admin/notifications`, {
          withCredentials: true
        })
      ]);
      setPromos(promosRes.data.campaigns || []);
      
      // Only update notifications if this is still the latest fetch
      // This prevents stale data from overwriting optimistic updates
      if (forceUpdate || currentVersion === notificationsFetchVersion.current) {
        setNotifications(notifsRes.data.notifications || []);
      }
    } catch (err) {
      console.error('Failed to fetch admin data:', err);
    }
  };

  // Fetch notification analytics
  const fetchNotificationAnalytics = async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/client-admin/notifications/analytics`, {
        withCredentials: true
      });
      setNotificationAnalytics(res.data);
    } catch (err) {
      console.error('Failed to fetch notification analytics:', err);
    }
  };

  // Fetch detailed notification analytics for a specific notification
  const openNotificationDetails = async (notif) => {
    setSelectedNotification(notif);
    try {
      const res = await axios.get(`${API_BASE}/api/client-admin/notifications/${notif.id}/analytics`, {
        withCredentials: true
      });
      setNotificationDetails(res.data);
    } catch (err) {
      console.error('Failed to fetch notification details:', err);
      toast.error('Failed to load notification details');
    }
  };

  // Fetch notification analytics when switching to notifications tab
  useEffect(() => {
    if (activeTab === 'notifications' && isAuthenticated && hasAdminAccess) {
      fetchNotificationAnalytics();
    }
  }, [activeTab, isAuthenticated, hasAdminAccess]);

  // Fetch detailed analytics when Analytics tab is active
  const fetchDetailedAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const tzOffset = -new Date().getTimezoneOffset() / 60;
      const res = await axios.get(`${API_BASE}/api/client-admin/analytics?tz_offset=${tzOffset}`, {
        withCredentials: true
      });
      setAnalyticsData(res.data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      if (err.response?.status !== 403) {
        toast.error('Failed to load analytics');
      }
    } finally {
      setAnalyticsLoading(false);
    }
  };

  // Fetch analytics when switching to analytics tab
  useEffect(() => {
    if (activeTab === 'analytics' && isAuthenticated && dashboard?.client?.features?.advanced_stats !== false) {
      fetchDetailedAnalytics();
    }
  }, [activeTab, isAuthenticated]);

  // Fetch banners when switching to banners tab
  const fetchBanners = async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/client-admin/banners`, {
        withCredentials: true
      });
      setBanners(res.data.banners || []);
    } catch (err) {
      console.error('Failed to fetch banners:', err);
    }
  };

  // Note: B2BBannerManager component handles its own banner data fetching internally
  // The banners state in ClientPortal is only used for initial data from login response

  // Initialize branding form when switching to branding tab - EXPANDED 8 COLORS
  useEffect(() => {
    if (activeTab === 'branding' && dashboard?.client?.theme) {
      const t = dashboard.client.theme;
      setBrandingForm({
        // Core colors
        brand_color: t.brand_color || t.primary_color || '#3B82F6',
        background_color: t.background_color || t.background_primary || '#0F172A',
        card_color: t.card_color || t.background_secondary || '#1E293B',
        text_color: t.text_color || t.text_primary || '#F8FAFC',
        // Additional colors for depth
        border_color: t.border_color || t.card_color || t.background_secondary || '#475569',
        accent_color: t.accent_color || t.brand_color || t.primary_color || '#22D3EE',
        secondary_text_color: t.secondary_text_color || t.text_secondary || '#94A3B8',
        input_background: t.input_background || t.card_color || t.background_secondary || '#1E293B'
      });
    }
  }, [activeTab, dashboard?.client?.theme]);

  const handleLogout = async () => {
    try {
      // Call server to invalidate session
      await axios.post(`${API_BASE}/api/client-portal/logout`, {}, {
        withCredentials: true
      });
    } catch (error) {
      // Continue with client-side logout even if server call fails
      console.error('Logout request failed:', error);
    }
    
    sessionStorage.removeItem('clientPortalName');
    
    setIsAuthenticated(false);
    setDashboard(null);
    setTheme(DEFAULT_THEME);
  };

  const handleInviteUser = async (e) => {
    e.preventDefault();
    
    
    try {
      await axios.post(`${API_BASE}/api/client-portal/invite-user`, {
        email: inviteEmail,
        first_name: inviteFirstName,
        last_name: inviteLastName
      }, {
        withCredentials: true
      });
      
      toast.success(`Invitation sent to ${inviteEmail}`);
      setShowInviteModal(false);
      setInviteEmail('');
      setInviteFirstName('');
      setInviteLastName('');
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invitation');
    }
  };

  const handleBulkInvite = async () => {
    
    
    if (bulkUsers.length === 0) {
      toast.error('No users to invite');
      return;
    }
    
    try {
      const res = await axios.post(`${API_BASE}/api/client-portal/bulk-invite`, {
        users: bulkUsers
      }, {
        withCredentials: true
      });
      
      toast.success(`Invited ${res.data.invited} users, skipped ${res.data.skipped}`);
      setShowBulkModal(false);
      setBulkUsers([]);
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invitations');
    }
  };

  const handleResendInvite = async (licenseUserId) => {
    
    
    try {
      await axios.post(`${API_BASE}/api/client-portal/resend-invite/${licenseUserId}`, {}, {
        withCredentials: true
      });
      toast.success('Invitation resent');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend');
    }
  };

  // Send login URL only (no password reset)
  const handleSendLoginUrl = async (licenseUserId) => {
    setShowUserCredentialsMenu(null);
    setSendingUserCredentials(licenseUserId);
    try {
      const res = await axios.post(`${API_BASE}/api/client-portal/users/${licenseUserId}/send-login-url`, {}, {
        withCredentials: true
      });
      if (res.data.email_sent) {
        toast.success('Login URL sent (password unchanged)');
      } else {
        toast.info('Login URL ready but email could not be sent');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send login URL');
    } finally {
      setSendingUserCredentials(null);
    }
  };

  // Reset password and send new credentials
  const handleResetUserPassword = async (licenseUserId) => {
    setShowUserCredentialsMenu(null);
    setSendingUserCredentials(licenseUserId);
    try {
      const res = await axios.post(`${API_BASE}/api/client-portal/users/${licenseUserId}/reset-password`, {}, {
        withCredentials: true
      });
      if (res.data.email_sent) {
        toast.success('New password sent to user');
      } else {
        toast.info('Password reset but email could not be sent');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setSendingUserCredentials(null);
    }
  };

  // Update user details (name)
  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    
    console.log('handleUpdateUser called with:', {
      license_user_id: selectedUser.license_user_id,
      first_name: editUserForm.first_name,
      last_name: editUserForm.last_name
    });
    
    if (!selectedUser.license_user_id) {
      console.error('No license_user_id found on selectedUser:', selectedUser);
      toast.error('User ID not found');
      return;
    }
    
    setSavingUserEdit(true);
    try {
      const url = `${API_BASE}/api/client-portal/users/${selectedUser.license_user_id}`;
      console.log('Making PUT request to:', url);
      
      await axios.put(url, {
        first_name: editUserForm.first_name,
        last_name: editUserForm.last_name
      }, { withCredentials: true });
      
      // Update the selected user state
      setSelectedUser(prev => ({
        ...prev,
        first_name: editUserForm.first_name,
        last_name: editUserForm.last_name
      }));
      
      // Refresh users list
      fetchDashboard();
      
      setIsEditingUser(false);
      toast.success('User updated successfully');
    } catch (err) {
      console.error('Update user failed:', err.response?.status, err.response?.data);
      toast.error(err.response?.data?.detail || 'Failed to update user');
    } finally {
      setSavingUserEdit(false);
    }
  };

  // ==================== PROMO HANDLERS ====================
  const handleImageUpload = async (e, type = 'image') => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const endpoint = type === 'video' ? '/api/upload/video' : '/api/upload/image';
      const res = await axios.post(`${API_BASE}${endpoint}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (type === 'video') {
        setPromoForm({ ...promoForm, video_url: res.data.url });
      } else {
        setPromoForm({ ...promoForm, image_url: res.data.url });
      }
      toast.success(`${type === 'video' ? 'Video' : 'Image'} uploaded`);
    } catch (err) {
      toast.error(`Failed to upload ${type}`);
    } finally {
      setUploading(false);
    }
  };

  const openPromoModal = (promo = null) => {
    if (promo) {
      setEditingPromo(promo);
      setPromoForm({
        banner_type: promo.banner_type || 'styled',
        title: promo.title || '',
        subtitle: promo.subtitle || '',
        description: promo.description || '',
        cta_text: promo.cta_text || 'Learn More',
        cta_link: promo.cta_link || '',
        discount_label: promo.discount_label || '',
        gradient_from: promo.gradient_from || theme.primary_color,
        gradient_to: promo.gradient_to || theme.secondary_color,
        image_url: promo.image_url || '',
        video_url: promo.video_url || '',
        is_active: promo.is_active || false,
        priority: promo.priority || 0
      });
    } else {
      setEditingPromo(null);
      setPromoForm({
        banner_type: 'styled',
        title: '',
        subtitle: '',
        description: '',
        cta_text: 'Learn More',
        cta_link: '',
        discount_label: '',
        gradient_from: theme.primary_color,
        gradient_to: theme.secondary_color,
        image_url: '',
        video_url: '',
        is_active: false,
        priority: 0
      });
    }
    setShowPromoModal(true);
  };

  const handleSavePromo = async () => {
    
    
    if (!promoForm.title) {
      toast.error('Title is required');
      return;
    }
    
    try {
      if (editingPromo) {
        await axios.put(`${API_BASE}/api/client-admin/promos/${editingPromo.id}`, promoForm, {
          withCredentials: true
        });
        toast.success('Campaign updated');
      } else {
        await axios.post(`${API_BASE}/api/client-admin/promos`, promoForm, {
          withCredentials: true
        });
        toast.success('Campaign created');
      }
      setShowPromoModal(false);
      fetchAdminData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save campaign');
    }
  };

  const handleTogglePromo = async (promoId) => {
    
    try {
      await axios.post(`${API_BASE}/api/client-admin/promos/${promoId}/toggle`, {}, {
        withCredentials: true
      });
      fetchAdminData();
    } catch (err) {
      toast.error('Failed to toggle campaign');
    }
  };

  const handleDeletePromo = async (promoId) => {
    if (!confirm('Delete this campaign?')) return;
    
    try {
      await axios.delete(`${API_BASE}/api/client-admin/promos/${promoId}`, {
        withCredentials: true
      });
      toast.success('Campaign deleted');
      fetchAdminData();
    } catch (err) {
      toast.error('Failed to delete campaign');
    }
  };

  // ==================== NOTIFICATION HANDLERS ====================
  const openNotifModal = (notif = null) => {
    if (notif) {
      setEditingNotif(notif);
      setNotifForm({
        title: notif.title || '',
        message: notif.message || '',
        type: notif.type || 'announcement',
        is_active: notif.is_active || false,
        dismissible: notif.dismissible !== false,
        show_popup: notif.show_popup !== false,
        image_url: notif.image_url || '',
        action_buttons: notif.action_buttons || [],
        target_type: notif.target_type || 'all',
        target_user_ids: notif.target_user_ids || [],
        target_filter: notif.target_filter || '',
        target_user_search: '',
        scheduled_at: notif.scheduled_at || '',
        expires_at: notif.expires_at || '',
        use_personalization: notif.use_personalization !== false
      });
      setNotifImagePreview(notif.image_url || null);
    } else {
      setEditingNotif(null);
      setNotifForm({
        title: '',
        message: '',
        type: 'announcement',
        is_active: true,
        dismissible: true,
        show_popup: true,
        image_url: '',
        action_buttons: [],
        target_type: 'all',
        target_user_ids: [],
        target_filter: '',
        target_user_search: '',
        scheduled_at: '',
        expires_at: '',
        use_personalization: true
      });
      setNotifImagePreview(null);
    }
    setShowNotifModal(true);
  };

  // Insert merge tag into message
  const insertMergeTag = (tag) => {
    setNotifForm({
      ...notifForm,
      message: notifForm.message + tag
    });
  };

  // Handle notification image upload
  const handleNotifImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Show preview
    const reader = new FileReader();
    reader.onload = (e) => setNotifImagePreview(e.target.result);
    reader.readAsDataURL(file);
    
    // Upload to server
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API_BASE}/api/client-admin/notifications/upload`, formData, {
        withCredentials: true
      });
      setNotifForm({ ...notifForm, image_url: res.data.url });
      toast.success('Image uploaded');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to upload image';
      toast.error(errorMsg);
      setNotifImagePreview(null);
      
      // Handle authentication failure
      if (err.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
        handleLogout();
      }
    }
  };

  const handleSaveNotif = async () => {
    
    
    if (!notifForm.title || !notifForm.message) {
      toast.error('Title and message are required');
      return;
    }
    
    try {
      if (editingNotif) {
        await axios.put(`${API_BASE}/api/client-admin/notifications/${editingNotif.id}`, notifForm, {
          withCredentials: true
        });
        toast.success('Notification updated');
      } else {
        await axios.post(`${API_BASE}/api/client-admin/notifications`, notifForm, {
          withCredentials: true
        });
        toast.success('Notification created');
      }
      setShowNotifModal(false);
      fetchAdminData(true); // Force update after intentional save
      fetchNotificationAnalytics();
    } catch (err) {
      const errorDetail = err.response?.data?.detail || 'Failed to save notification';
      toast.error(errorDetail);
      
      // If authentication failed, redirect to login
      if (err.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
        handleLogout();
      }
    }
  };

  const handleToggleNotif = async (notifId) => {
    
    const notif = notifications.find(n => n.id === notifId);
    if (!notif) return;
    
    try {
      await axios.put(`${API_BASE}/api/client-admin/notifications/${notifId}`, {
        is_active: !notif.is_active
      }, {
        withCredentials: true
      });
      fetchAdminData(true); // Force update after intentional toggle
      fetchNotificationAnalytics();
    } catch (err) {
      toast.error('Failed to toggle notification');
    }
  };

  // Fetch user details and stats
  const handleUserClick = async (user) => {
    setSelectedUser(user);
    setShowUserDetailModal(true);
    setLoadingUserStats(true);
    
    // In preview mode, use mock data instead of API call
    if (isPreviewMode) {
      setUserStats({
        total_entries: Math.floor(Math.random() * 50) + 5,
        current_streak: Math.floor(Math.random() * 14),
        longest_streak: Math.floor(Math.random() * 30) + 7,
        last_entry_date: new Date().toISOString(),
        badges_earned: Math.floor(Math.random() * 8),
        mood_average: (Math.random() * 2 + 3).toFixed(1)
      });
      setLoadingUserStats(false);
      return;
    }
    
    try {
      // Fetch user stats from API
      const res = await axios.get(`${API_BASE}/api/client-admin/users/${user.license_user_id}/stats`, {
        withCredentials: true
      });
      setUserStats(res.data);
    } catch (err) {
      console.error('Failed to fetch user stats:', err);
      // Set default stats if API fails
      setUserStats({
        total_entries: 0,
        current_streak: 0,
        longest_streak: 0,
        last_entry_date: null,
        badges_earned: 0,
        mood_average: null
      });
    } finally {
      setLoadingUserStats(false);
    }
  };

  const handleDeleteNotif = async (notifId) => {
    if (!confirm('Delete this notification?')) return;
    
    // Bump version to invalidate any in-flight fetchAdminData calls
    notificationsFetchVersion.current++;
    
    // Optimistically remove from UI immediately
    const previousNotifications = [...notifications];
    setNotifications(prev => prev.filter(n => n.id !== notifId));
    
    try {
      await axios.delete(`${API_BASE}/api/client-admin/notifications/${notifId}`, {
        withCredentials: true
      });
      toast.success('Notification deleted');
      // Refresh analytics after successful delete (doesn't affect notification list)
      fetchNotificationAnalytics();
    } catch (err) {
      // Revert optimistic update on error
      setNotifications(previousNotifications);
      console.error('Delete notification error:', err.response?.data || err.message);
      toast.error(err.response?.data?.detail || 'Failed to delete notification');
    }
  };

  // Delete a license user
  const handleDeleteUser = async (licenseUserId, userEmail) => {
    if (!confirm(`Remove ${userEmail} from your organization? This will revoke their access.`)) return;
    
    try {
      await axios.delete(`${API_BASE}/api/client-admin/users/${licenseUserId}`, {
        withCredentials: true
      });
      toast.success(`${userEmail} has been removed`);
      await fetchAdminData();
    } catch (err) {
      toast.error('Failed to remove user');
    }
  };

  // ==================== BANNER HANDLERS ====================
  const openBannerModal = (banner = null) => {
    if (banner) {
      setEditingBanner(banner);
      setBannerForm({
        title: banner.title || '',
        subtitle: banner.subtitle || '',
        cta_text: banner.cta_text || 'Learn More',
        cta_link: banner.cta_link || '',
        image_url: banner.image_url || '',
        gradient_from: banner.gradient_from || theme.primary_color,
        gradient_to: banner.gradient_to || theme.secondary_color,
        position: banner.position || 'main',
        is_active: banner.is_active || false,
        priority: banner.priority || 0
      });
    } else {
      setEditingBanner(null);
      setBannerForm({
        title: '',
        subtitle: '',
        cta_text: 'Learn More',
        cta_link: '',
        image_url: '',
        gradient_from: theme.primary_color,
        gradient_to: theme.secondary_color,
        position: 'main',
        is_active: false,
        priority: 0
      });
    }
    setShowBannerModal(true);
  };

  const handleSaveBanner = async () => {
    if (!bannerForm.title) {
      toast.error('Title is required');
      return;
    }
    
    try {
      if (editingBanner) {
        await axios.put(`${API_BASE}/api/client-admin/banners/${editingBanner.id}`, bannerForm, {
          withCredentials: true
        });
        toast.success('Banner updated');
      } else {
        await axios.post(`${API_BASE}/api/client-admin/banners`, bannerForm, {
          withCredentials: true
        });
        toast.success('Banner created');
      }
      setShowBannerModal(false);
      fetchBanners();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save banner');
    }
  };

  const handleToggleBanner = async (bannerId) => {
    try {
      await axios.post(`${API_BASE}/api/client-admin/banners/${bannerId}/toggle`, {}, {
        withCredentials: true
      });
      fetchBanners();
    } catch (err) {
      toast.error('Failed to toggle banner');
    }
  };

  const handleDeleteBanner = async (bannerId) => {
    if (!confirm('Delete this banner?')) return;
    
    try {
      await axios.delete(`${API_BASE}/api/client-admin/banners/${bannerId}`, {
        withCredentials: true
      });
      toast.success('Banner deleted');
      fetchBanners();
    } catch (err) {
      toast.error('Failed to delete banner');
    }
  };

  // ==================== BRANDING HANDLERS ====================
  const handleSaveBranding = async () => {
    setSavingBranding(true);
    try {
      const response = await axios.put(`${API_BASE}/api/client-admin/theme`, {
        theme: brandingForm
      }, {
        withCredentials: true
      });
      
      // Update local theme with the FULL merged theme from backend
      // This ensures all legacy fields are properly mapped from the new 4-color values
      if (response.data.theme) {
        setTheme(mergeThemeWithDefaults(response.data.theme));
      }
      
      toast.success('Branding updated successfully');
      
      // Refresh dashboard to get updated theme
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save branding');
    } finally {
      setSavingBranding(false);
    }
  };

  const handleBannerImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await axios.post(`${API_BASE}/api/upload/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setBannerForm({ ...bannerForm, image_url: res.data.url });
      toast.success('Image uploaded');
    } catch (err) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const parseCSV = (csvText) => {
    const lines = csvText.trim().split('\n');
    const headers = lines[0].toLowerCase().split(',').map(h => h.trim());
    const emailIdx = headers.findIndex(h => h.includes('email'));
    const firstNameIdx = headers.findIndex(h => h.includes('first') || h.includes('fname'));
    const lastNameIdx = headers.findIndex(h => h.includes('last') || h.includes('lname'));
    
    if (emailIdx === -1) {
      toast.error('CSV must have an "email" column');
      return [];
    }
    
    if (firstNameIdx === -1 || lastNameIdx === -1) {
      toast.error('CSV must have "first_name" and "last_name" columns (all fields are required)');
      return [];
    }
    
    const users = [];
    const errors = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
      const email = values[emailIdx];
      const firstName = values[firstNameIdx];
      const lastName = values[lastNameIdx];
      
      if (!email || !email.includes('@')) {
        if (email) errors.push(`Row ${i+1}: invalid email`);
        continue;
      }
      if (!firstName || !lastName) {
        errors.push(`Row ${i+1}: missing name fields`);
        continue;
      }
      
      users.push({
        email: email.toLowerCase(),
        first_name: firstName,
        last_name: lastName
      });
    }
    
    if (errors.length > 0) {
      toast.warning(`Skipped ${errors.length} rows with missing data`);
    }
    
    return users;
  };

  const handleCSVUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const users = parseCSV(event.target.result);
      setBulkUsers(users);
      if (users.length > 0) {
        toast.success(`Parsed ${users.length} users from CSV`);
      }
    };
    reader.readAsText(file);
  };

  const filteredUsers = dashboard?.users?.filter(u => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return u.email?.toLowerCase().includes(q) || 
           u.first_name?.toLowerCase().includes(q) ||
           u.last_name?.toLowerCase().includes(q);
  }) || [];

  const themeStyles = getThemeStyles(theme);

  // Login Screen - EXACT MATCH to regular login page with B2B indicator
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-[#2D063E] to-[#4A0A66] p-4 overflow-x-hidden">
        {/* Animation keyframes */}
        <style>{`
          @keyframes gradient-x {
            0%, 100% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
          }
          .animate-gradient-x {
            animation: gradient-x 3s ease infinite;
          }
        `}</style>

        <div className="flex-1 flex flex-col items-center justify-center">
          <div className="text-center max-w-lg w-full px-2">
            {/* Logo with gradient */}
            <div className="w-full px-4 mb-2">
              <div 
                className="w-full h-24 sm:h-28 lg:h-32 animate-gradient-x mx-auto max-w-md lg:max-w-lg"
                style={{
                  background: 'linear-gradient(90deg, #df1283 0%, #FF7901 25%, #df1283 50%, #FF7901 75%, #df1283 100%)',
                  backgroundSize: '200% 100%',
                  WebkitMaskImage: `url("/new-logo-homepage.png")`,
                  WebkitMaskSize: 'contain',
                  WebkitMaskRepeat: 'no-repeat',
                  WebkitMaskPosition: 'center',
                  maskImage: `url("/new-logo-homepage.png")`,
                  maskSize: 'contain',
                  maskRepeat: 'no-repeat',
                  maskPosition: 'center',
                }}
              />
            </div>
            
            <p className="text-sm sm:text-base text-[#FDF2F8]/80 mb-6 px-2">
              Your private morning journal with mood tracking and AI coaching.
            </p>

            {/* Glass card */}
            <div className="glass-card-brand p-6 sm:p-8">
              {/* B2B Client Portal Badge */}
              <div className="flex items-center justify-center gap-2 mb-6 py-2.5 px-5 rounded-full mx-auto w-fit bg-[#0FFFE2]/10 border border-[#0FFFE2]/30">
                <Building2 className="w-4 h-4 text-[#0FFFE2]" />
                <span className="text-sm font-medium text-[#0FFFE2]">B2B Client Portal</span>
              </div>

              <form onSubmit={handleLogin} className="space-y-5">
                {/* Client ID */}
                <div>
                  <input
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value)}
                    placeholder="Client ID or Email"
                    className="w-full px-4 py-3.5 rounded-xl text-white placeholder-[#FDF2F8]/50 focus:outline-none text-sm"
                    style={{
                      backgroundColor: 'rgba(74, 10, 102, 0.3)',
                      border: '1px solid rgba(223, 18, 131, 0.3)',
                    }}
                    data-testid="client-id-input"
                  />
                </div>

                {/* Password */}
                <div>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Portal Password"
                    className="w-full px-4 py-3.5 rounded-xl text-white placeholder-[#FDF2F8]/50 focus:outline-none text-sm"
                    style={{
                      backgroundColor: 'rgba(74, 10, 102, 0.3)',
                      border: '1px solid rgba(223, 18, 131, 0.3)',
                    }}
                    data-testid="portal-password-input"
                  />
                </div>

                {/* Error */}
                {loginError && (
                  <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 text-red-300 text-sm">
                    {loginError}
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading || !clientId || !password}
                  className="w-full py-3.5 rounded-xl font-semibold transition-all bg-gradient-to-r from-[#df1283] to-[#FF7901] text-white shadow-lg hover:opacity-90 disabled:opacity-50 text-sm uppercase tracking-wide"
                  data-testid="client-login-submit"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Sign In to Portal'}
                </button>
              </form>

              {/* Divider and link to regular login */}
              <div className="mt-8 pt-6 border-t border-[#df1283]/20">
                <p className="text-center text-sm text-[#FDF2F8]/60">
                  Not a B2B partner? <a href="/login" className="text-[#0FFFE2] hover:underline font-medium">Sign in here</a>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <Footer />
      </div>
    );
  }

  // Dashboard
  const client = dashboard?.client;
  const stats = dashboard?.stats;
  // Always use client's app name for branding, with proper fallback
  const appName = client?.app_name || client?.name || 'Journal';
  
  // Helper to get mood label - uses custom mood label for "uncloned" or "custom_branded" if available
  const MOOD_LABELS = {
    uncloned: 'Custom', custom_branded: 'Custom', on_fire: 'On Fire', magical: 'Magical', amazing: 'Amazing',
    loved: 'Loved', hopeful: 'Hopeful', dreamy: 'Dreamy', free: 'Free',
    peaceful: 'Peaceful', energized: 'Energized', calm: 'Calm', thoughtful: 'Thoughtful',
    focused: 'Focused', tired: 'Tired', melancholy: 'Melancholy', strong: 'Strong',
    angry: 'Angry', sad: 'Sad', grateful: 'Grateful', creative: 'Creative', happy: 'Happy'
  };
  
  const getMoodLabel = (mood) => {
    // If this is the custom mood and client has a custom label configured
    if ((mood === 'uncloned' || mood === 'custom_branded') && client?.custom_mood_enabled && client?.custom_mood_label) {
      return client.custom_mood_label;
    }
    return MOOD_LABELS[mood] || mood?.replace(/_/g, ' ')?.replace(/\b\w/g, c => c.toUpperCase()) || 'Unknown';
  };

  return (
    <div 
      className="min-h-screen p-4 md:p-8 overflow-x-hidden"
      style={{
        ...themeStyles,
        background: theme.background_primary || DEFAULT_THEME.background_primary, // SOLID color, no gradient
        minHeight: '100vh',
        position: 'relative',
        zIndex: 1
      }}
    >
      <div className="max-w-6xl mx-auto overflow-hidden">
        {/* Client Branding - Show logo if uploaded, otherwise show company name with gradient effect */}
        <div className="flex justify-center mb-6 px-4">
          {client?.logo_url ? (
            <img 
              src={client.logo_url} 
              alt={client.name} 
              className="max-h-20 sm:max-h-28 md:max-h-36 lg:max-h-40 w-auto object-contain"
              style={{ maxWidth: '90vw' }}
              data-testid="client-logo"
            />
          ) : (
            <h1 
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-center"
              style={{
                color: theme.primary_color || DEFAULT_THEME.primary_color // SOLID color, no gradient
              }}
              data-testid="client-app-name"
            >
              {appName}
            </h1>
          )}
        </div>

        {/* Preview Mode Banner */}
        {isPreviewMode && (
          <div 
            className="mb-6 px-4 py-3 rounded-lg flex items-center justify-between"
            style={{
              background: `${theme.primary_color}15`, // Subtle solid tint
              border: `2px dashed ${theme.primary_color}60`
            }}
          >
            <div className="flex items-center gap-3">
              <Eye className="w-5 h-5" style={{ color: theme.primary_color }} />
              <div>
                <p className="font-semibold text-sm" style={{ color: theme.text_primary }}>
                  Preview Mode — This is what your B2B client sees
                </p>
                <p className="text-xs" style={{ color: theme.text_secondary }}>
                  Changes made here won't be saved. This is a read-only preview.
                </p>
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => window.close()}
              style={{ 
                borderColor: theme.primary_color,
                color: theme.primary_color
              }}
              className="hover:opacity-80"
            >
              Close Preview
            </Button>
          </div>
        )}

        {/* Header */}
        <header className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 gap-4">
          <div className="flex items-center gap-3 sm:gap-4">
            <div 
              className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center text-white font-bold text-lg sm:text-xl flex-shrink-0"
              style={{ background: theme.primary_color }} // SOLID color
            >
              {client?.name?.charAt(0) || 'C'}
            </div>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-bold truncate" style={{ color: theme.text_primary }}>{client?.name || 'Client'} Portal</h1>
              <p className="text-xs sm:text-sm truncate" style={{ color: theme.text_secondary }}>Manage your {appName.toLowerCase()} program</p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 self-end sm:self-auto">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              size="sm"
              style={{ color: theme.text_secondary }}
              className="hover:opacity-100 px-2 sm:px-3"
              title="Back to Main Site"
            >
              <span className="text-sm mr-1">←</span> <span className="hidden sm:inline">Back</span>
            </Button>
            <Button
              onClick={() => fetchDashboard()}
              variant="ghost"
              size="sm"
              style={{ color: theme.text_secondary }}
              className="hover:opacity-100 px-2 sm:px-3"
              disabled={isPreviewMode}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            {!isPreviewMode && (
              <Button
                onClick={handleLogout}
                variant="ghost"
                size="sm"
                style={{ color: theme.text_secondary }}
                className="hover:opacity-100 px-2 sm:px-3"
                data-testid="client-portal-logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            )}
          </div>
        </header>

        {loading && !dashboard ? (
          <div className="flex items-center justify-center py-20">
            <div 
              className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: theme.accent_color, borderTopColor: 'transparent' }}
            />
          </div>
        ) : (
          <>
            {/* License Info Banner */}
            <div 
              className="rounded-xl p-4 mb-6"
              style={{
                background: `${theme.primary_color}15`, // Subtle solid tint
                border: `1px solid ${theme.primary_color}30`
              }}
            >
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <p className="text-sm" style={{ color: theme.text_secondary }}>License Period</p>
                  <p className="font-medium" style={{ color: theme.text_primary }}>
                    {client?.start_date && format(new Date(client.start_date), 'MMM d, yyyy')} — {client?.end_date && format(new Date(client.end_date), 'MMM d, yyyy')}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm" style={{ color: theme.text_secondary }}>Days Remaining</p>
                  <p 
                    className="text-xl font-bold"
                    style={{ color: client?.days_remaining > 30 ? theme.accent_color : theme.warning_color }}
                  >
                    {client?.days_remaining || 0}
                  </p>
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              <div 
                className="rounded-xl p-4"
                style={{
                  background: theme.background_secondary, // SOLID color,
                  border: `1px solid ${theme.border_color}`
                }}
              >
                <div className="text-2xl font-bold" style={{ color: theme.text_primary }}>{client?.total_licenses || 0}</div>
                <div className="text-sm" style={{ color: theme.text_secondary }}>Total Seats</div>
              </div>
              <div 
                className="rounded-xl p-4"
                style={{
                  background: theme.background_secondary, // SOLID color,
                  border: `1px solid ${theme.border_color}`
                }}
              >
                <div className="text-2xl font-bold" style={{ color: theme.success_color }}>{stats?.activated || 0}</div>
                <div className="text-sm" style={{ color: theme.text_secondary }}>Activated</div>
              </div>
              <div 
                className="rounded-xl p-4"
                style={{
                  background: theme.background_secondary, // SOLID color,
                  border: `1px solid ${theme.border_color}`
                }}
              >
                <div className="text-2xl font-bold" style={{ color: theme.warning_color }}>{stats?.pending || 0}</div>
                <div className="text-sm" style={{ color: theme.text_secondary }}>Pending</div>
              </div>
              <div 
                className="rounded-xl p-4"
                style={{
                  background: theme.background_secondary, // SOLID color,
                  border: `1px solid ${theme.border_color}`
                }}
              >
                <div className="text-2xl font-bold" style={{ color: theme.accent_color }}>{stats?.remaining_seats || 0}</div>
                <div className="text-sm" style={{ color: theme.text_secondary }}>Available</div>
              </div>
              <div 
                className="rounded-xl p-4"
                style={{
                  background: theme.background_secondary, // SOLID color,
                  border: `1px solid ${theme.border_color}`
                }}
              >
                <div className="text-2xl font-bold" style={{ color: theme.primary_color }}>{stats?.entries_today || 0}</div>
                <div className="text-sm" style={{ color: theme.text_secondary }}>Entries Today</div>
              </div>
            </div>

            {/* Client Login URL Section - Prominent placement for easy sharing */}
            <div 
              className="mb-6 rounded-xl p-4 md:p-5"
              style={{
                background: `${theme.primary_color}15`,
                border: `2px solid ${theme.primary_color}40`
              }}
              data-testid="client-login-url-section"
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: theme.primary_color }}
                  >
                    <LinkIcon className="w-5 h-5" style={{ color: theme.text_on_brand || '#ffffff' }} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-base" style={{ color: theme.text_primary }}>
                      Your Client Login URL
                    </h3>
                    <p className="text-sm mt-0.5" style={{ color: theme.text_secondary }}>
                      Share this link with your clients so they can log in to their journals
                    </p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 md:gap-3">
                  <div 
                    className="flex-1 px-3 py-2 rounded-lg text-sm font-mono truncate min-w-0"
                    style={{ 
                      backgroundColor: `${theme.background_primary}`,
                      border: `1px solid ${theme.border_color}`,
                      color: theme.text_primary
                    }}
                  >
                    {window.location.origin}/portal/{client?.client_id}/login
                  </div>
                  <Button
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/portal/${client?.client_id}/login`);
                      toast.success('Client login URL copied to clipboard!');
                    }}
                    className="flex items-center justify-center gap-2 whitespace-nowrap"
                    style={{
                      background: theme.primary_color, // SOLID color,
                      color: theme.text_on_brand || 'white'
                    }}
                    data-testid="copy-client-url-btn"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                    </svg>
                    Copy URL
                  </Button>
                </div>
              </div>
              <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${theme.border_color}40` }}>
                <p className="text-xs" style={{ color: theme.text_secondary }}>
                  <strong>How it works:</strong> Clients visit this URL, enter their email and password (set during invite), and access their personal journal. 
                  They'll see your company branding and can start journaling immediately.
                </p>
              </div>
            </div>

            {/* Tab Navigation - USES background_secondary (same as card_color) - SOLID COLOR */}
            {hasAdminAccess && (
              <div 
                className="flex items-center gap-2 mb-6 p-1 rounded-lg overflow-x-auto"
                data-testid="client-portal-tab-nav"
                style={{ 
                  backgroundColor: theme.background_secondary,
                  scrollbarWidth: 'thin',
                  WebkitOverflowScrolling: 'touch'
                }}
              >
                <button
                  onClick={() => setActiveTab('users')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium text-sm transition-all whitespace-nowrap flex-shrink-0`}
                  style={{
                    backgroundColor: activeTab === 'users' ? theme.background_secondary : 'transparent',
                    color: activeTab === 'users' ? theme.text_primary : theme.text_secondary,
                    border: activeTab === 'users' ? `1px solid ${theme.border_color}` : '1px solid transparent'
                  }}
                  data-testid="tab-users"
                >
                  <Users className="w-4 h-4" />
                  Users
                </button>
                {/* Analytics Tab - Before Promo Campaigns */}
                <button
                  onClick={() => setActiveTab('analytics')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium text-sm transition-all whitespace-nowrap flex-shrink-0`}
                  style={{
                    backgroundColor: activeTab === 'analytics' ? theme.background_secondary : 'transparent',
                    color: activeTab === 'analytics' ? theme.text_primary : theme.text_secondary,
                    border: activeTab === 'analytics' ? `1px solid ${theme.border_color}` : '1px solid transparent'
                  }}
                  data-testid="tab-analytics"
                >
                  <BarChart3 className="w-4 h-4" />
                  Analytics
                  {dashboard?.client?.features?.advanced_stats === false && (
                    <Lock className="w-3 h-3" style={{ color: theme.warning_color }} />
                  )}
                </button>
                {/* Promos Tab - ONLY show if main_promo_banner (carousel) feature is enabled */}
                {dashboard?.client?.features?.main_promo_banner !== false && (
                  <button
                    onClick={() => setActiveTab('promos')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium text-sm transition-all whitespace-nowrap flex-shrink-0`}
                    style={{
                      backgroundColor: activeTab === 'promos' ? theme.background_secondary : 'transparent',
                      color: activeTab === 'promos' ? theme.text_primary : theme.text_secondary,
                      border: activeTab === 'promos' ? `1px solid ${theme.border_color}` : '1px solid transparent'
                    }}
                    data-testid="tab-promos"
                  >
                    <Megaphone className="w-4 h-4" />
                    Promo Campaigns
                    {promos.length > 0 && (
                      <span 
                        className="text-xs px-1.5 rounded-full"
                        style={{ backgroundColor: `${theme.accent_color}33`, color: theme.accent_color }}
                      >
                        {promos.length}
                      </span>
                    )}
                  </button>
                )}
                {/* Notifications Tab - ONLY show if feature is enabled */}
                {dashboard?.client?.features?.popup_notifications !== false && (
                  <button
                    onClick={() => setActiveTab('notifications')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium text-sm transition-all whitespace-nowrap flex-shrink-0`}
                    style={{
                      backgroundColor: activeTab === 'notifications' ? theme.background_secondary : 'transparent',
                      color: activeTab === 'notifications' ? theme.text_primary : theme.text_secondary,
                      border: activeTab === 'notifications' ? `1px solid ${theme.border_color}` : '1px solid transparent'
                    }}
                    data-testid="tab-notifications"
                  >
                    <Bell className="w-4 h-4" />
                    Notifications
                  </button>
                )}
                <button
                  onClick={() => setActiveTab('features')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium text-sm transition-all whitespace-nowrap flex-shrink-0`}
                  style={{
                    backgroundColor: activeTab === 'features' ? theme.background_secondary : 'transparent',
                    color: activeTab === 'features' ? theme.text_primary : theme.text_secondary,
                    border: activeTab === 'features' ? `1px solid ${theme.border_color}` : '1px solid transparent'
                  }}
                  data-testid="tab-features"
                >
                  <Settings className="w-4 h-4" />
                  Features
                </button>
                {/* Banners Tab - ONLY show if at least one banner feature is enabled */}
                {(dashboard?.client?.features?.main_promo_banner !== false || dashboard?.client?.features?.sidebar_promo !== false) && (
                  <button
                    onClick={() => setActiveTab('banners')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium text-sm transition-all whitespace-nowrap flex-shrink-0`}
                    style={{
                      backgroundColor: activeTab === 'banners' ? theme.background_secondary : 'transparent',
                      color: activeTab === 'banners' ? theme.text_primary : theme.text_secondary,
                      border: activeTab === 'banners' ? `1px solid ${theme.border_color}` : '1px solid transparent'
                    }}
                    data-testid="tab-banners"
                  >
                    <ImageIcon className="w-4 h-4" />
                    Banners
                  </button>
                )}
                {/* Branding Tab - ONLY show if custom themes feature is enabled */}
                {dashboard?.client?.features?.custom_themes !== false && (
                  <button
                    onClick={() => setActiveTab('branding')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium text-sm transition-all whitespace-nowrap flex-shrink-0`}
                    style={{
                      backgroundColor: activeTab === 'branding' ? theme.background_secondary : 'transparent',
                      color: activeTab === 'branding' ? theme.text_primary : theme.text_secondary,
                      border: activeTab === 'branding' ? `1px solid ${theme.border_color}` : '1px solid transparent'
                    }}
                    data-testid="tab-branding"
                  >
                    <Palette className="w-4 h-4" />
                    Branding
                  </button>
                )}
                {/* Video Embeds Tab - ONLY show if feature is enabled */}
                {dashboard?.client?.features?.video_embeds !== false && (
                  <button
                    onClick={() => setActiveTab('videos')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium text-sm transition-all whitespace-nowrap flex-shrink-0`}
                    style={{
                      backgroundColor: activeTab === 'videos' ? theme.background_secondary : 'transparent',
                      color: activeTab === 'videos' ? theme.text_primary : theme.text_secondary,
                      border: activeTab === 'videos' ? `1px solid ${theme.border_color}` : '1px solid transparent'
                    }}
                    data-testid="tab-videos"
                  >
                    <Video className="w-4 h-4" />
                    Videos
                  </button>
                )}
              </div>
            )}

            {/* ==================== USERS TAB ==================== */}
            {activeTab === 'users' && (
              <>
            {/* Actions & Search */}
            <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
              <div className="relative flex-1 max-w-md">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: theme.accent_color }} />
                <style>{`
                  .search-input-themed::placeholder {
                    color: ${theme.accent_color} !important;
                    opacity: 0.7;
                  }
                `}</style>
                <Input
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 search-input-themed"
                  style={{
                    backgroundColor: theme.background_secondary,
                    borderColor: theme.border_color,
                    color: theme.text_primary
                  }}
                />
              </div>
              <div className="flex items-center gap-2">
                {!isPreviewMode && (
                  <>
                    <Button
                      onClick={() => setShowBulkModal(true)}
                      variant="outline"
                      style={{
                        borderColor: theme.accent_color,
                        color: theme.accent_color
                      }}
                      className="hover:opacity-80"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload CSV
                    </Button>
                    <Button
                      onClick={() => setShowInviteModal(true)}
                      style={{
                        background: theme.primary_color,
                        color: theme.text_on_brand || '#ffffff'
                      }}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add User
                    </Button>
                  </>
                )}
                {isPreviewMode && (
                  <span className="text-xs px-3 py-2 rounded-lg" style={{ 
                    background: `${theme.warning_color}20`, 
                    color: theme.warning_color 
                  }}>
                    Actions disabled in preview
                  </span>
                )}
              </div>
            </div>

            {/* Users List */}
            <div 
              className="rounded-xl overflow-hidden"
              style={{
                background: theme.background_secondary, // SOLID color,
                border: `1px solid ${theme.border_color}`
              }}
            >
              <div className="p-4" style={{ borderBottom: `1px solid ${theme.border_color}` }}>
                <h2 className="text-lg font-semibold flex items-center gap-2" style={{ color: theme.text_primary }}>
                  <Users className="w-5 h-5" style={{ color: theme.accent_color }} />
                  Users ({filteredUsers.length})
                </h2>
              </div>
              <div className="max-h-[500px] overflow-y-auto" style={{ borderColor: `${theme.border_color}80` }}>
                {filteredUsers.length === 0 ? (
                  <div className="text-center py-12" style={{ color: theme.text_secondary }}>
                    {dashboard?.users?.length === 0 
                      ? 'No users yet. Click "Add User" or "Upload CSV" to invite users.'
                      : 'No users match your search.'}
                  </div>
                ) : (
                  filteredUsers.map((user, idx) => (
                    <div 
                      key={user.license_user_id} 
                      className="flex items-center justify-between p-4"
                      style={{
                        borderTop: idx > 0 ? `1px solid ${theme.border_color}50` : 'none',
                        backgroundColor: 'transparent'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = `${theme.border_color}33`}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      {/* Clickable user info area */}
                      <div 
                        className="flex items-center gap-3 flex-1 cursor-pointer"
                        onClick={() => handleUserClick(user)}
                        data-testid={`user-row-${user.license_user_id}`}
                      >
                        {user.status === 'activated' ? (
                          <UserCheck className="w-5 h-5" style={{ color: theme.success_color }} />
                        ) : (
                          <UserX className="w-5 h-5" style={{ color: theme.warning_color }} />
                        )}
                        <div>
                          <div className="font-medium" style={{ color: theme.text_primary }}>
                            {user.first_name ? `${user.first_name} ${user.last_name || ''}` : user.email}
                          </div>
                          <div className="text-xs" style={{ color: theme.text_secondary }}>{user.email}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <span 
                            className="text-xs px-2 py-1 rounded-full"
                            style={{
                              backgroundColor: user.status === 'activated' 
                                ? `${theme.success_color}33`
                                : `${theme.warning_color}33`,
                              color: user.status === 'activated' ? theme.success_color : theme.warning_color
                            }}
                          >
                            {user.status === 'activated' ? 'Activated' : 'Pending'}
                          </span>
                          <div className="text-xs mt-1" style={{ color: theme.text_secondary }}>
                            {user.status === 'activated' && user.activated_at
                              ? `Activated ${format(new Date(user.activated_at), 'MMM d')}`
                              : user.invited_at 
                                ? `Invited ${format(new Date(user.invited_at), 'MMM d')}`
                                : ''}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {user.status === 'pending' && !isPreviewMode && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => { e.stopPropagation(); handleResendInvite(user.license_user_id); }}
                              style={{ color: theme.accent_color }}
                              className="hover:opacity-80"
                              title="Resend invitation"
                            >
                              <Send className="w-4 h-4" />
                            </Button>
                          )}
                          {user.status === 'activated' && !isPreviewMode && (
                            <div className="relative">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  setShowUserCredentialsMenu(showUserCredentialsMenu === user.license_user_id ? null : user.license_user_id);
                                }}
                                disabled={sendingUserCredentials === user.license_user_id}
                                style={{ color: theme.accent_color }}
                                className="hover:opacity-80"
                                title="Send credentials"
                              >
                                {sendingUserCredentials === user.license_user_id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Send className="w-4 h-4" />
                                )}
                              </Button>
                              
                              {showUserCredentialsMenu === user.license_user_id && (
                                <div 
                                  className="absolute right-0 mt-1 min-w-[240px] rounded-lg shadow-xl z-50"
                                  style={{ 
                                    backgroundColor: theme.background_secondary,
                                    border: `1px solid ${theme.border_color}`,
                                    whiteSpace: 'nowrap'
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <button
                                    onClick={() => handleSendLoginUrl(user.license_user_id)}
                                    className="w-full px-4 py-3 text-left text-sm hover:opacity-80 flex items-center gap-3"
                                    style={{ 
                                      color: theme.text_primary,
                                      borderBottom: `1px solid ${theme.border_color}50`
                                    }}
                                  >
                                    <LinkIcon className="w-4 h-4 flex-shrink-0" style={{ color: theme.accent_color }} />
                                    <div className="flex-1">
                                      <div className="font-medium whitespace-nowrap">Send Login URL</div>
                                      <div className="text-xs whitespace-nowrap" style={{ color: theme.text_secondary }}>Keep existing password</div>
                                    </div>
                                  </button>
                                  <button
                                    onClick={() => handleResetUserPassword(user.license_user_id)}
                                    className="w-full px-4 py-3 text-left text-sm hover:opacity-80 flex items-center gap-3"
                                    style={{ color: theme.text_primary }}
                                  >
                                    <Send className="w-4 h-4 flex-shrink-0" style={{ color: theme.warning_color }} />
                                    <div className="flex-1">
                                      <div className="font-medium whitespace-nowrap">Reset Password</div>
                                      <div className="text-xs whitespace-nowrap" style={{ color: theme.text_secondary }}>Generate new password</div>
                                    </div>
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                          {!isPreviewMode && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => { e.stopPropagation(); handleDeleteUser(user.license_user_id, user.email); }}
                              className="hover:opacity-80 text-red-400 hover:text-red-300"
                              title="Remove user"
                              data-testid={`delete-user-${user.license_user_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
              </>
            )}

            {/* ==================== PROMO CAMPAIGNS TAB ==================== */}
            {activeTab === 'promos' && hasAdminAccess && dashboard?.client?.features?.main_promo_banner !== false && (
              <B2BPromoCampaignManager theme={theme} />
            )}

            {/* ==================== NOTIFICATIONS TAB ==================== */}
            {activeTab === 'notifications' && hasAdminAccess && dashboard?.client?.features?.popup_notifications !== false && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold flex items-center gap-2" style={{ color: theme.text_primary }}>
                    <Bell className="w-5 h-5" style={{ color: theme.accent_color }} />
                    Push Notifications
                  </h2>
                  <Button
                    onClick={() => openNotifModal()}
                    style={{
                      background: theme.primary_color,
                      color: theme.text_on_brand || '#ffffff'
                    }}
                    data-testid="create-notif-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    New Notification
                  </Button>
                </div>

                {/* Analytics Summary Cards */}
                {notificationAnalytics && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                    <div 
                      className="rounded-xl p-4 text-center"
                      style={{
                        background: theme.background_secondary, // SOLID color,
                        border: `1px solid ${theme.border_color}`
                      }}
                    >
                      <div className="text-2xl font-bold" style={{ color: theme.text_primary }}>{notificationAnalytics.summary?.total_sent || 0}</div>
                      <div className="text-xs" style={{ color: theme.text_secondary }}>Total Sent</div>
                    </div>
                    <div 
                      className="rounded-xl p-4 text-center"
                      style={{
                        background: theme.background_secondary, // SOLID color,
                        border: `1px solid ${theme.border_color}`
                      }}
                    >
                      <div className="text-2xl font-bold" style={{ color: theme.primary_color }}>{notificationAnalytics.summary?.view_rate || 0}%</div>
                      <div className="text-xs" style={{ color: theme.text_secondary }}>View Rate</div>
                      <div className="text-xs" style={{ color: `${theme.primary_color}B3` }}>{notificationAnalytics.summary?.total_views || 0} views</div>
                    </div>
                    <div 
                      className="rounded-xl p-4 text-center"
                      style={{
                        background: theme.background_secondary, // SOLID color,
                        border: `1px solid ${theme.border_color}`
                      }}
                    >
                      <div className="text-2xl font-bold" style={{ color: theme.accent_color }}>{notificationAnalytics.summary?.click_rate || 0}%</div>
                      <div className="text-xs" style={{ color: theme.text_secondary }}>Click Rate</div>
                      <div className="text-xs" style={{ color: `${theme.accent_color}B3` }}>{notificationAnalytics.summary?.total_clicks || 0} clicks</div>
                    </div>
                    <div 
                      className="rounded-xl p-4 text-center"
                      style={{
                        background: theme.background_secondary, // SOLID color,
                        border: `1px solid ${theme.border_color}`
                      }}
                    >
                      <div className="text-2xl font-bold" style={{ color: theme.secondary_color }}>{notificationAnalytics.counts?.active || 0}</div>
                      <div className="text-xs" style={{ color: theme.text_secondary }}>Active</div>
                      <div className="text-xs" style={{ color: `${theme.secondary_color}B3` }}>{notificationAnalytics.counts?.total || 0} total</div>
                    </div>
                  </div>
                )}

                {notifications.length === 0 ? (
                  <div 
                    className="rounded-xl p-12 text-center"
                    style={{
                      background: theme.background_secondary, // SOLID color,
                      border: `1px solid ${theme.border_color}`
                    }}
                  >
                    <Bell className="w-12 h-12 mx-auto mb-4" style={{ color: theme.text_secondary }} />
                    <p style={{ color: theme.text_secondary }}>No notifications yet</p>
                    <p className="text-sm mt-1" style={{ color: theme.text_secondary }}>
                      Create push notifications for your users
                    </p>
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {notifications.map((notif) => (
                      <div 
                        key={notif.id}
                        className="rounded-xl p-4 cursor-pointer hover:opacity-90 transition-opacity"
                        style={{
                          background: theme.background_secondary, // SOLID color,
                          border: `1px solid ${theme.border_color}`
                        }}
                        onClick={() => openNotificationDetails(notif)}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span className="font-semibold" style={{ color: theme.text_primary }}>{notif.title}</span>
                              <span 
                                className="text-xs px-2 py-0.5 rounded-full capitalize"
                                style={{
                                  backgroundColor: notif.type === 'success' ? `${theme.success_color}33` 
                                    : notif.type === 'warning' ? `${theme.warning_color}33`
                                    : notif.type === 'error' ? `${theme.error_color}33`
                                    : `${theme.accent_color}33`,
                                  color: notif.type === 'success' ? theme.success_color 
                                    : notif.type === 'warning' ? theme.warning_color
                                    : notif.type === 'error' ? theme.error_color
                                    : theme.accent_color
                                }}
                              >
                                {notif.type}
                              </span>
                              <span 
                                className="text-xs px-2 py-0.5 rounded-full"
                                style={{
                                  backgroundColor: notif.is_active ? `${theme.success_color}33` : `${theme.text_secondary}33`,
                                  color: notif.is_active ? theme.success_color : theme.text_secondary
                                }}
                              >
                                {notif.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                            <p className="text-sm" style={{ color: theme.text_secondary }}>{notif.message}</p>
                            {/* Stats row */}
                            <div className="flex items-center gap-4 mt-3 text-xs flex-wrap">
                              <span style={{ color: theme.text_secondary }}>
                                {notif.created_at ? new Date(notif.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A'}
                              </span>
                              <span className="flex items-center gap-1" style={{ color: theme.text_primary }}>
                                <Users className="w-3 h-3" />
                                {notif.users_notified || 0} recipients
                              </span>
                              {(notif.views > 0) && (
                                <span className="flex items-center gap-1" style={{ color: theme.primary_color }}>
                                  <Eye className="w-3 h-3" />
                                  {notif.views} views
                                </span>
                              )}
                              {(notif.clicks > 0) && (
                                <span className="flex items-center gap-1" style={{ color: theme.accent_color }}>
                                  <MousePointer className="w-3 h-3" />
                                  {notif.clicks} clicks
                                </span>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleNotif(notif.id)}
                              style={{ color: notif.is_active ? theme.success_color : theme.text_secondary }}
                              title={notif.is_active ? 'Deactivate' : 'Activate'}
                            >
                              {notif.is_active ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openNotifModal(notif)}
                              style={{ color: theme.accent_color }}
                              title="Edit"
                            >
                              <Settings className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteNotif(notif.id)}
                              style={{ color: theme.error_color }}
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Notification Details Modal */}
                {selectedNotification && notificationDetails && (
                  <div 
                    className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
                    onClick={() => { setSelectedNotification(null); setNotificationDetails(null); }}
                  >
                    <div 
                      className="w-full max-w-lg rounded-2xl overflow-hidden"
                      style={{
                        background: theme.background_primary,
                        border: `1px solid ${theme.border_color}`
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* Header */}
                      <div 
                        className="px-6 py-4 flex items-center justify-between"
                        style={{ borderBottom: `1px solid ${theme.border_color}` }}
                      >
                        <h3 className="font-semibold" style={{ color: theme.text_primary }}>
                          Notification Analytics
                        </h3>
                        <button
                          onClick={() => { setSelectedNotification(null); setNotificationDetails(null); }}
                          className="p-1 rounded-full hover:bg-white/10"
                          style={{ color: theme.text_secondary }}
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                      
                      {/* Content */}
                      <div className="p-6 space-y-6">
                        {/* Notification Preview */}
                        <div>
                          <h4 className="text-sm font-medium mb-2" style={{ color: theme.text_secondary }}>Notification</h4>
                          <div 
                            className="p-4 rounded-xl"
                            style={{ 
                              background: theme.background_secondary,
                              border: `1px solid ${theme.border_color}`
                            }}
                          >
                            <p className="font-semibold" style={{ color: theme.text_primary }}>{selectedNotification.title}</p>
                            <p className="text-sm mt-1" style={{ color: theme.text_secondary }}>{selectedNotification.message}</p>
                          </div>
                        </div>

                        {/* Stats Grid */}
                        <div className="grid grid-cols-3 gap-4">
                          <div 
                            className="p-4 rounded-xl text-center"
                            style={{ 
                              background: theme.background_secondary,
                              border: `1px solid ${theme.border_color}`
                            }}
                          >
                            <div className="text-2xl font-bold" style={{ color: theme.text_primary }}>{notificationDetails.total_recipients || 0}</div>
                            <div className="text-xs" style={{ color: theme.text_secondary }}>Recipients</div>
                          </div>
                          <div 
                            className="p-4 rounded-xl text-center"
                            style={{ 
                              background: theme.background_secondary,
                              border: `1px solid ${theme.border_color}`
                            }}
                          >
                            <div className="text-2xl font-bold" style={{ color: theme.primary_color }}>{notificationDetails.view_count || 0}</div>
                            <div className="text-xs" style={{ color: theme.text_secondary }}>Views</div>
                            <div className="text-xs" style={{ color: `${theme.primary_color}B3` }}>
                              {notificationDetails.total_recipients > 0 
                                ? Math.round((notificationDetails.view_count || 0) / notificationDetails.total_recipients * 100) 
                                : 0}% rate
                            </div>
                          </div>
                          <div 
                            className="p-4 rounded-xl text-center"
                            style={{ 
                              background: theme.background_secondary,
                              border: `1px solid ${theme.border_color}`
                            }}
                          >
                            <div className="text-2xl font-bold" style={{ color: theme.accent_color }}>{notificationDetails.click_count || 0}</div>
                            <div className="text-xs" style={{ color: theme.text_secondary }}>Clicks</div>
                            <div className="text-xs" style={{ color: `${theme.accent_color}B3` }}>
                              {notificationDetails.total_recipients > 0 
                                ? Math.round((notificationDetails.click_count || 0) / notificationDetails.total_recipients * 100) 
                                : 0}% rate
                            </div>
                          </div>
                        </div>

                        {/* Viewed Users List */}
                        {notificationDetails.viewed_users?.length > 0 && (
                          <div>
                            <h4 className="text-sm font-medium mb-2 flex items-center gap-2" style={{ color: theme.text_secondary }}>
                              <Eye className="w-4 h-4" style={{ color: theme.primary_color }} />
                              Viewed By ({notificationDetails.viewed_users.length})
                            </h4>
                            <div 
                              className="max-h-32 overflow-y-auto rounded-xl p-3 space-y-2"
                              style={{ 
                                background: theme.background_secondary,
                                border: `1px solid ${theme.border_color}`
                              }}
                            >
                              {notificationDetails.viewed_users.slice(0, 10).map((user, idx) => (
                                <div key={user.user_id || `viewed-${idx}`} className="flex items-center justify-between text-sm">
                                  <span style={{ color: theme.text_primary }}>{user.name || user.email}</span>
                                  <span className="text-xs" style={{ color: theme.text_secondary }}>
                                    {user.timestamp ? new Date(user.timestamp).toLocaleDateString() : ''}
                                  </span>
                                </div>
                              ))}
                              {notificationDetails.viewed_users.length > 10 && (
                                <p className="text-xs text-center" style={{ color: theme.text_secondary }}>
                                  +{notificationDetails.viewed_users.length - 10} more
                                </p>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Clicked Users List */}
                        {notificationDetails.clicked_users?.length > 0 && (
                          <div>
                            <h4 className="text-sm font-medium mb-2 flex items-center gap-2" style={{ color: theme.text_secondary }}>
                              <MousePointer className="w-4 h-4" style={{ color: theme.accent_color }} />
                              Clicked By ({notificationDetails.clicked_users.length})
                            </h4>
                            <div 
                              className="max-h-32 overflow-y-auto rounded-xl p-3 space-y-2"
                              style={{ 
                                background: theme.background_secondary,
                                border: `1px solid ${theme.border_color}`
                              }}
                            >
                              {notificationDetails.clicked_users.slice(0, 10).map((user, idx) => (
                                <div key={user.user_id || `clicked-${idx}`} className="flex items-center justify-between text-sm">
                                  <span style={{ color: theme.text_primary }}>{user.name || user.email}</span>
                                  <span className="text-xs" style={{ color: theme.text_secondary }}>
                                    {user.timestamp ? new Date(user.timestamp).toLocaleDateString() : ''}
                                  </span>
                                </div>
                              ))}
                              {notificationDetails.clicked_users.length > 10 && (
                                <p className="text-xs text-center" style={{ color: theme.text_secondary }}>
                                  +{notificationDetails.clicked_users.length - 10} more
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Footer */}
                      <div 
                        className="px-6 py-4 flex justify-end"
                        style={{ borderTop: `1px solid ${theme.border_color}` }}
                      >
                        <Button
                          onClick={() => { setSelectedNotification(null); setNotificationDetails(null); }}
                          style={{
                            background: theme.primary_color,
                            color: theme.text_on_brand || '#ffffff'
                          }}
                        >
                          Close
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ==================== FEATURES TAB ==================== */}
            {activeTab === 'features' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-semibold flex items-center gap-2" style={{ color: theme.text_primary }}>
                    <Settings className="w-5 h-5" style={{ color: theme.accent_color }} />
                    Feature Access Control
                  </h2>
                </div>

                <div 
                  className="rounded-xl p-4 mb-6"
                  style={{
                    background: `${theme.primary_color}15`,
                    border: `1px solid ${theme.border_color}`
                  }}
                >
                  <p className="text-sm" style={{ color: theme.text_secondary }}>
                    Features below are managed by your licensing agreement. Click any locked feature to request an upgrade.
                  </p>
                </div>

                {/* Feature Categories */}
                <div className="space-y-6">
                  {/* Core Journal Features */}
                  <div>
                    <h3 className="text-sm font-semibold uppercase tracking-wider mb-3 flex items-center gap-2" style={{ color: getSectionHeaderColor(theme.background_primary, theme.text_primary, theme.accent_color) }}>
                      <FileText className="w-4 h-4" />
                      Core Journal Features
                    </h3>
                    <div className="grid gap-3">
                      {[
                        { key: 'voice_typing', name: 'Voice Typing', icon: Mic, description: 'Record journal entries using voice-to-text' },
                        { key: 'ai_coach', name: 'AI Coach Reflections', icon: Brain, description: 'Get AI-powered insights on journal entries' },
                        { key: 'pdf_export', name: 'PDF Export', icon: FileText, description: 'Export journal entries to PDF format' },
                        { key: 'mood_tracking', name: 'Mood Tracking', icon: Smile, description: 'Track daily mood with entries' },
                        { key: 'image_attachments', name: 'Image Attachments', icon: ImageIcon, description: 'Attach images to journal entries' },
                        { key: 'link_preview', name: 'Link Preview', icon: Link, description: 'Preview links in journal entries' },
                      ].map(feature => {
                        const isEnabled = dashboard?.client?.features?.[feature.key] !== false;
                        const Icon = feature.icon;
                        return (
                          <div 
                            key={feature.key}
                            className={`flex items-center justify-between p-4 rounded-xl transition-all ${!isEnabled ? 'cursor-pointer hover:opacity-90' : ''}`}
                            style={{
                              background: theme.background_secondary,
                              border: `1px solid ${theme.border_color}`
                            }}
                            onClick={() => !isEnabled && setShowUpgradeModal(true)}
                          >
                            <div className="flex items-center gap-3">
                              <div 
                                className="w-10 h-10 rounded-lg flex items-center justify-center"
                                style={{ 
                                  backgroundColor: isEnabled ? `${theme.success_color}20` : `${theme.text_secondary}20`
                                }}
                              >
                                <Icon className="w-5 h-5" style={{ color: isEnabled ? theme.success_color : `${theme.text_secondary}60` }} />
                              </div>
                              <div>
                                <p className="font-medium" style={{ color: theme.text_primary }}>{feature.name}</p>
                                <p className="text-xs" style={{ color: theme.text_secondary }}>{feature.description}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {isEnabled ? (
                                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ backgroundColor: `${theme.success_color}20` }}>
                                  <CheckCircle className="w-4 h-4" style={{ color: theme.success_color }} />
                                  <span className="text-sm font-medium" style={{ color: theme.success_color }}>Active</span>
                                </div>
                              ) : (
                                <button 
                                  className="flex items-center gap-2 px-3 py-1.5 rounded-full transition-all hover:scale-105"
                                  style={{ backgroundColor: `${theme.warning_color}20` }}
                                  onClick={(e) => { e.stopPropagation(); setShowUpgradeModal(true); }}
                                >
                                  <Lock className="w-4 h-4" style={{ color: theme.warning_color }} />
                                  <span className="text-sm font-medium" style={{ color: theme.warning_color }}>Locked</span>
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Gamification Features */}
                  <div>
                    <h3 className="text-sm font-semibold uppercase tracking-wider mb-3 flex items-center gap-2" style={{ color: getSectionHeaderColor(theme.background_primary, theme.text_primary, theme.secondary_color) }}>
                      <Trophy className="w-4 h-4" />
                      Gamification
                    </h3>
                    <div className="grid gap-3">
                      {[
                        { key: 'gamification', name: 'Gamification System', icon: Sparkles, description: 'Points, achievements, and rewards' },
                        { key: 'streaks', name: 'Streak Tracking', icon: Flame, description: 'Track consecutive journaling days' },
                        { key: 'badges', name: 'Achievement Badges', icon: Award, description: 'Earn badges for milestones' },
                      ].map(feature => {
                        const isEnabled = dashboard?.client?.features?.[feature.key] !== false;
                        const Icon = feature.icon;
                        return (
                          <div 
                            key={feature.key}
                            className={`flex items-center justify-between p-4 rounded-xl transition-all ${!isEnabled ? 'cursor-pointer hover:opacity-90' : ''}`}
                            style={{
                              background: theme.background_secondary,
                              border: `1px solid ${theme.border_color}`
                            }}
                            onClick={() => !isEnabled && setShowUpgradeModal(true)}
                          >
                            <div className="flex items-center gap-3">
                              <div 
                                className="w-10 h-10 rounded-lg flex items-center justify-center"
                                style={{ 
                                  backgroundColor: isEnabled ? `${theme.success_color}20` : `${theme.text_secondary}20`
                                }}
                              >
                                <Icon className="w-5 h-5" style={{ color: isEnabled ? theme.success_color : `${theme.text_secondary}60` }} />
                              </div>
                              <div>
                                <p className="font-medium" style={{ color: theme.text_primary }}>{feature.name}</p>
                                <p className="text-xs" style={{ color: theme.text_secondary }}>{feature.description}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {isEnabled ? (
                                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ backgroundColor: `${theme.success_color}20` }}>
                                  <CheckCircle className="w-4 h-4" style={{ color: theme.success_color }} />
                                  <span className="text-sm font-medium" style={{ color: theme.success_color }}>Active</span>
                                </div>
                              ) : (
                                <button 
                                  className="flex items-center gap-2 px-3 py-1.5 rounded-full transition-all hover:scale-105"
                                  style={{ backgroundColor: `${theme.warning_color}20` }}
                                  onClick={(e) => { e.stopPropagation(); setShowUpgradeModal(true); }}
                                >
                                  <Lock className="w-4 h-4" style={{ color: theme.warning_color }} />
                                  <span className="text-sm font-medium" style={{ color: theme.warning_color }}>Locked</span>
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Promotional & Marketing */}
                  <div>
                    <h3 className="text-sm font-semibold uppercase tracking-wider mb-3 flex items-center gap-2" style={{ color: getSectionHeaderColor(theme.background_primary, theme.text_primary, theme.primary_color) }}>
                      <Megaphone className="w-4 h-4" />
                      Promotional & Marketing
                    </h3>
                    <div className="grid gap-3">
                      {[
                        { key: 'sidebar_promo', name: 'Sidebar Promotions', icon: Megaphone, description: 'Display promo banners to users' },
                        { key: 'main_promo_banner', name: 'Main Banner Carousel', icon: ImageIcon, description: 'Featured banner carousel on dashboard' },
                        { key: 'push_notifications', name: 'Push Notifications', icon: Bell, description: 'Send notifications to your users' },
                        { key: 'referral_system', name: 'Referral System', icon: Users, description: 'User referral tracking and rewards' },
                      ].map(feature => {
                        const isEnabled = dashboard?.client?.features?.[feature.key] !== false;
                        const Icon = feature.icon;
                        return (
                          <div 
                            key={feature.key}
                            className={`flex items-center justify-between p-4 rounded-xl transition-all ${!isEnabled ? 'cursor-pointer hover:opacity-90' : ''}`}
                            style={{
                              background: theme.background_secondary,
                              border: `1px solid ${theme.border_color}`
                            }}
                            onClick={() => !isEnabled && setShowUpgradeModal(true)}
                          >
                            <div className="flex items-center gap-3">
                              <div 
                                className="w-10 h-10 rounded-lg flex items-center justify-center"
                                style={{ 
                                  backgroundColor: isEnabled ? `${theme.success_color}20` : `${theme.text_secondary}20`
                                }}
                              >
                                <Icon className="w-5 h-5" style={{ color: isEnabled ? theme.success_color : `${theme.text_secondary}60` }} />
                              </div>
                              <div>
                                <p className="font-medium" style={{ color: theme.text_primary }}>{feature.name}</p>
                                <p className="text-xs" style={{ color: theme.text_secondary }}>{feature.description}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {isEnabled ? (
                                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ backgroundColor: `${theme.success_color}20` }}>
                                  <CheckCircle className="w-4 h-4" style={{ color: theme.success_color }} />
                                  <span className="text-sm font-medium" style={{ color: theme.success_color }}>Active</span>
                                </div>
                              ) : (
                                <button 
                                  className="flex items-center gap-2 px-3 py-1.5 rounded-full transition-all hover:scale-105"
                                  style={{ backgroundColor: `${theme.warning_color}20` }}
                                  onClick={(e) => { e.stopPropagation(); setShowUpgradeModal(true); }}
                                >
                                  <Lock className="w-4 h-4" style={{ color: theme.warning_color }} />
                                  <span className="text-sm font-medium" style={{ color: theme.warning_color }}>Locked</span>
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Appearance */}
                  <div>
                    <h3 className="text-sm font-semibold uppercase tracking-wider mb-3 flex items-center gap-2" style={{ color: getSectionHeaderColor(theme.background_primary, theme.text_primary, theme.accent_color) }}>
                      <Palette className="w-4 h-4" />
                      Appearance
                    </h3>
                    <div className="grid gap-3">
                      {[
                        { key: 'themes', name: 'Theme Customization', icon: Palette, description: 'Custom colors and branding' },
                        { key: 'dark_mode', name: 'Dark Mode', icon: Moon, description: 'Toggle dark theme for users' },
                      ].map(feature => {
                        const isEnabled = dashboard?.client?.features?.[feature.key] !== false;
                        const Icon = feature.icon;
                        return (
                          <div 
                            key={feature.key}
                            className={`flex items-center justify-between p-4 rounded-xl transition-all ${!isEnabled ? 'cursor-pointer hover:opacity-90' : ''}`}
                            style={{
                              background: theme.background_secondary,
                              border: `1px solid ${theme.border_color}`
                            }}
                            onClick={() => !isEnabled && setShowUpgradeModal(true)}
                          >
                            <div className="flex items-center gap-3">
                              <div 
                                className="w-10 h-10 rounded-lg flex items-center justify-center"
                                style={{ 
                                  backgroundColor: isEnabled ? `${theme.success_color}20` : `${theme.text_secondary}20`
                                }}
                              >
                                <Icon className="w-5 h-5" style={{ color: isEnabled ? theme.success_color : `${theme.text_secondary}60` }} />
                              </div>
                              <div>
                                <p className="font-medium" style={{ color: theme.text_primary }}>{feature.name}</p>
                                <p className="text-xs" style={{ color: theme.text_secondary }}>{feature.description}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {isEnabled ? (
                                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ backgroundColor: `${theme.success_color}20` }}>
                                  <CheckCircle className="w-4 h-4" style={{ color: theme.success_color }} />
                                  <span className="text-sm font-medium" style={{ color: theme.success_color }}>Active</span>
                                </div>
                              ) : (
                                <button 
                                  className="flex items-center gap-2 px-3 py-1.5 rounded-full transition-all hover:scale-105"
                                  style={{ backgroundColor: `${theme.warning_color}20` }}
                                  onClick={(e) => { e.stopPropagation(); setShowUpgradeModal(true); }}
                                >
                                  <Lock className="w-4 h-4" style={{ color: theme.warning_color }} />
                                  <span className="text-sm font-medium" style={{ color: theme.warning_color }}>Locked</span>
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Premium Features */}
                  <div>
                    <h3 className="text-sm font-semibold uppercase tracking-wider mb-3 flex items-center gap-2" style={{ color: getSectionHeaderColor(theme.background_primary, theme.text_primary, theme.success_color) }}>
                      <Crown className="w-4 h-4" />
                      Premium Features
                    </h3>
                    <div className="grid gap-3">
                      {[
                        { key: 'advanced_stats', name: 'Advanced Analytics', icon: BarChart3, description: 'Detailed usage and engagement metrics' },
                      ].map(feature => {
                        const isEnabled = dashboard?.client?.features?.[feature.key] !== false;
                        const Icon = feature.icon;
                        return (
                          <div 
                            key={feature.key}
                            className={`flex items-center justify-between p-4 rounded-xl transition-all ${!isEnabled ? 'cursor-pointer hover:opacity-90' : ''}`}
                            style={{
                              background: theme.background_secondary,
                              border: `1px solid ${theme.border_color}`
                            }}
                            onClick={() => !isEnabled && setShowUpgradeModal(true)}
                          >
                            <div className="flex items-center gap-3">
                              <div 
                                className="w-10 h-10 rounded-lg flex items-center justify-center"
                                style={{ 
                                  backgroundColor: isEnabled ? `${theme.success_color}20` : `${theme.text_secondary}20`
                                }}
                              >
                                <Icon className="w-5 h-5" style={{ color: isEnabled ? theme.success_color : `${theme.text_secondary}60` }} />
                              </div>
                              <div>
                                <p className="font-medium" style={{ color: theme.text_primary }}>{feature.name}</p>
                                <p className="text-xs" style={{ color: theme.text_secondary }}>{feature.description}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {isEnabled ? (
                                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ backgroundColor: `${theme.success_color}20` }}>
                                  <CheckCircle className="w-4 h-4" style={{ color: theme.success_color }} />
                                  <span className="text-sm font-medium" style={{ color: theme.success_color }}>Active</span>
                                </div>
                              ) : (
                                <button 
                                  className="flex items-center gap-2 px-3 py-1.5 rounded-full transition-all hover:scale-105"
                                  style={{ backgroundColor: `${theme.warning_color}20` }}
                                  onClick={(e) => { e.stopPropagation(); setShowUpgradeModal(true); }}
                                >
                                  <Lock className="w-4 h-4" style={{ color: theme.warning_color }} />
                                  <span className="text-sm font-medium" style={{ color: theme.warning_color }}>Locked</span>
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Upgrade CTA for locked features */}
                <div 
                  className="rounded-xl p-6 mt-6 text-center"
                  style={{
                    background: `${theme.primary_color}20`,
                    border: `1px solid ${theme.border_color}`
                  }}
                >
                  <Crown className="w-10 h-10 mx-auto mb-3" style={{ color: theme.warning_color }} />
                  <h3 className="text-lg font-semibold mb-2" style={{ color: theme.text_primary }}>
                    Need More Features?
                  </h3>
                  <p className="text-sm mb-4" style={{ color: theme.text_secondary }}>
                    Contact your account administrator to upgrade your plan and unlock additional features.
                  </p>
                  <button 
                    onClick={() => setShowUpgradeModal(false)}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all hover:opacity-90"
                    style={{
                      background: theme.primary_color,
                      color: theme.text_on_brand || '#ffffff'
                    }}
                  >
                    <Send className="w-4 h-4" />
                    Got It
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* ==================== ANALYTICS TAB ==================== */}
        {activeTab === 'analytics' && (
          <div data-testid="analytics-tab-content">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold flex items-center gap-2" style={{ color: theme.text_primary }}>
                <BarChart3 className="w-5 h-5" style={{ color: theme.accent_color }} />
                Analytics Dashboard
              </h2>
              <Button
                onClick={fetchDetailedAnalytics}
                variant="ghost"
                size="sm"
                disabled={analyticsLoading}
                style={{ color: theme.text_secondary }}
                className="hover:opacity-100"
                data-testid="refresh-analytics-btn"
              >
                <RefreshCw className={`w-4 h-4 ${analyticsLoading ? 'animate-spin' : ''}`} />
              </Button>
            </div>

            {/* Check if analytics feature is enabled */}
            {dashboard?.client?.features?.advanced_stats === false ? (
              /* Locked State */
              <div 
                className="rounded-xl p-8 text-center"
                style={{
                  background: theme.background_secondary,
                  border: `1px solid ${theme.border_color}`
                }}
              >
                <div 
                  className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center"
                  style={{ backgroundColor: `${theme.warning_color}20` }}
                >
                  <Lock className="w-10 h-10" style={{ color: theme.warning_color }} />
                </div>
                <h3 className="text-xl font-bold mb-3" style={{ color: theme.text_primary }}>
                  Analytics Locked
                </h3>
                <p className="text-sm mb-6 max-w-md mx-auto" style={{ color: theme.text_secondary }}>
                  Advanced analytics is not included in your current plan. Upgrade to access detailed insights about your users' journaling activity.
                </p>
                <button 
                  onClick={() => setShowUpgradeModal(true)}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all hover:opacity-90"
                  style={{
                    background: theme.primary_color,
                    color: theme.text_on_brand || '#ffffff'
                  }}
                >
                  <Crown className="w-5 h-5" />
                  Upgrade to Unlock
                </button>
              </div>
            ) : analyticsLoading && !analyticsData ? (
              /* Loading State */
              <div className="flex items-center justify-center py-20">
                <div 
                  className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin"
                  style={{ borderColor: theme.accent_color, borderTopColor: 'transparent' }}
                />
              </div>
            ) : (
              /* Active State - Show Detailed Analytics */
              <div className="space-y-6">
                {/* Top Summary Stats */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div 
                    className="rounded-xl p-4"
                    style={{
                      background: theme.background_secondary,
                      border: `1px solid ${theme.border_color}`
                    }}
                    data-testid="stat-total-users"
                  >
                    <p className="text-xs uppercase tracking-wider mb-1" style={{ color: theme.text_secondary }}>Total Users</p>
                    <p className="text-2xl font-bold" style={{ color: theme.text_primary }}>{analyticsData?.summary?.total_users || dashboard?.stats?.total_seats || 0}</p>
                  </div>
                  <div 
                    className="rounded-xl p-4"
                    style={{
                      background: theme.background_secondary,
                      border: `1px solid ${theme.border_color}`
                    }}
                    data-testid="stat-activated-users"
                  >
                    <p className="text-xs uppercase tracking-wider mb-1" style={{ color: theme.text_secondary }}>Activated</p>
                    <p className="text-2xl font-bold" style={{ color: theme.success_color }}>{analyticsData?.summary?.activated_users || dashboard?.stats?.activated || 0}</p>
                  </div>
                  <div 
                    className="rounded-xl p-4"
                    style={{
                      background: theme.background_secondary,
                      border: `1px solid ${theme.border_color}`
                    }}
                    data-testid="stat-engagement-rate"
                  >
                    <p className="text-xs uppercase tracking-wider mb-1" style={{ color: theme.text_secondary }}>Engagement</p>
                    <p className="text-2xl font-bold" style={{ color: theme.accent_color }}>{analyticsData?.summary?.engagement_rate || 0}%</p>
                  </div>
                  <div 
                    className="rounded-xl p-4"
                    style={{
                      background: theme.background_secondary,
                      border: `1px solid ${theme.border_color}`
                    }}
                    data-testid="stat-total-entries"
                  >
                    <p className="text-xs uppercase tracking-wider mb-1" style={{ color: theme.text_secondary }}>Total Entries</p>
                    <p className="text-2xl font-bold" style={{ color: theme.primary_color }}>{analyticsData?.entries?.total || 0}</p>
                  </div>
                  <div 
                    className="rounded-xl p-4"
                    style={{
                      background: theme.background_secondary,
                      border: `1px solid ${theme.border_color}`
                    }}
                    data-testid="stat-avg-per-user"
                  >
                    <p className="text-xs uppercase tracking-wider mb-1" style={{ color: theme.text_secondary }}>Avg/User</p>
                    <p className="text-2xl font-bold" style={{ color: theme.secondary_color }}>{analyticsData?.entries?.average_per_user || 0}</p>
                  </div>
                </div>

                {/* Entries Breakdown & 7-Day Trend */}
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Entries Breakdown */}
                  <div 
                    className="rounded-xl p-6"
                    style={{
                      background: theme.background_secondary,
                      border: `1px solid ${theme.border_color}`
                    }}
                    data-testid="entries-breakdown"
                  >
                    <h3 className="text-md font-semibold mb-4 flex items-center gap-2" style={{ color: theme.text_primary }}>
                      <FileText className="w-4 h-4" style={{ color: theme.accent_color }} />
                      Journal Entries
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-3 rounded-lg" style={{ backgroundColor: `${theme.background_tertiary}50` }}>
                        <p className="text-xs uppercase tracking-wider mb-1" style={{ color: theme.text_secondary }}>Today</p>
                        <p className="text-xl font-bold" style={{ color: theme.success_color }}>{analyticsData?.entries?.today || 0}</p>
                      </div>
                      <div className="p-3 rounded-lg" style={{ backgroundColor: `${theme.background_tertiary}50` }}>
                        <p className="text-xs uppercase tracking-wider mb-1" style={{ color: theme.text_secondary }}>This Week</p>
                        <p className="text-xl font-bold" style={{ color: theme.accent_color }}>{analyticsData?.entries?.this_week || 0}</p>
                      </div>
                      <div className="p-3 rounded-lg" style={{ backgroundColor: `${theme.background_tertiary}50` }}>
                        <p className="text-xs uppercase tracking-wider mb-1" style={{ color: theme.text_secondary }}>This Month</p>
                        <p className="text-xl font-bold" style={{ color: theme.primary_color }}>{analyticsData?.entries?.this_month || 0}</p>
                      </div>
                      <div className="p-3 rounded-lg" style={{ backgroundColor: `${theme.background_tertiary}50` }}>
                        <p className="text-xs uppercase tracking-wider mb-1" style={{ color: theme.text_secondary }}>Users w/ Entries</p>
                        <p className="text-xl font-bold" style={{ color: theme.secondary_color }}>{analyticsData?.summary?.users_with_entries || 0}</p>
                      </div>
                    </div>
                  </div>

                  {/* 7-Day Trend Chart */}
                  <div 
                    className="rounded-xl p-6"
                    style={{
                      background: theme.background_secondary,
                      border: `1px solid ${theme.border_color}`
                    }}
                    data-testid="daily-trend-chart"
                  >
                    <h3 className="text-md font-semibold mb-4 flex items-center gap-2" style={{ color: theme.text_primary }}>
                      <BarChart3 className="w-4 h-4" style={{ color: theme.accent_color }} />
                      7-Day Activity Trend
                    </h3>
                    <div className="flex items-end gap-2 h-32">
                      {(analyticsData?.daily_trend || []).map((day, idx) => {
                        const maxEntries = Math.max(...(analyticsData?.daily_trend || []).map(d => d.entries), 1);
                        const height = (day.entries / maxEntries) * 100;
                        return (
                          <div key={day.day || `day-${idx}`} className="flex-1 flex flex-col items-center gap-1">
                            <div 
                              className="w-full rounded-t transition-all duration-300 hover:opacity-80"
                              style={{ 
                                height: `${Math.max(height, 5)}%`,
                                background: theme.primary_color,
                                minHeight: '4px'
                              }}
                              title={`${day.entries} entries`}
                            />
                            <span className="text-xs" style={{ color: theme.text_secondary }}>{day.day}</span>
                          </div>
                        );
                      })}
                      {(!analyticsData?.daily_trend || analyticsData.daily_trend.length === 0) && (
                        <p className="text-sm text-center w-full py-8" style={{ color: `${theme.text_secondary}60` }}>
                          No activity data yet
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Feature Usage & Mood Stats */}
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Feature Usage */}
                  <div 
                    className="rounded-xl p-6"
                    style={{
                      background: theme.background_secondary,
                      border: `1px solid ${theme.border_color}`
                    }}
                    data-testid="feature-usage"
                  >
                    <h3 className="text-md font-semibold mb-4 flex items-center gap-2" style={{ color: theme.text_primary }}>
                      <Sparkles className="w-4 h-4" style={{ color: theme.accent_color }} />
                      Feature Usage
                    </h3>
                    <div className="space-y-4">
                      {/* Voice Typing */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-8 h-8 rounded-lg flex items-center justify-center"
                            style={{ backgroundColor: `${theme.primary_color}20` }}
                          >
                            <Mic className="w-4 h-4" style={{ color: theme.primary_color }} />
                          </div>
                          <span className="text-sm" style={{ color: theme.text_primary }}>Voice Entries</span>
                        </div>
                        <span className="text-lg font-bold" style={{ color: theme.primary_color }}>
                          {analyticsData?.feature_usage?.voice_typing || 0}
                        </span>
                      </div>
                      
                      {/* AI Coach */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-8 h-8 rounded-lg flex items-center justify-center"
                            style={{ backgroundColor: `${theme.secondary_color}20` }}
                          >
                            <Brain className="w-4 h-4" style={{ color: theme.secondary_color }} />
                          </div>
                          <span className="text-sm" style={{ color: theme.text_primary }}>AI Coach Uses</span>
                        </div>
                        <span className="text-lg font-bold" style={{ color: theme.secondary_color }}>
                          {analyticsData?.feature_usage?.ai_coach || 0}
                        </span>
                      </div>
                      
                      {/* PDF Exports */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-8 h-8 rounded-lg flex items-center justify-center"
                            style={{ backgroundColor: `${theme.accent_color}20` }}
                          >
                            <FileText className="w-4 h-4" style={{ color: theme.accent_color }} />
                          </div>
                          <span className="text-sm" style={{ color: theme.text_primary }}>PDF Exports</span>
                        </div>
                        <span className="text-lg font-bold" style={{ color: theme.accent_color }}>
                          {analyticsData?.feature_usage?.pdf_exports || 0}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Mood Stats */}
                  <div 
                    className="rounded-xl p-6"
                    style={{
                      background: theme.background_secondary,
                      border: `1px solid ${theme.border_color}`
                    }}
                    data-testid="mood-stats"
                  >
                    <h3 className="text-md font-semibold mb-4 flex items-center gap-2" style={{ color: theme.text_primary }}>
                      <Smile className="w-4 h-4" style={{ color: theme.accent_color }} />
                      Mood Analytics
                    </h3>
                    {analyticsData?.mood?.average != null ? (
                      <div className="space-y-4">
                        {/* Average Mood */}
                        <div className="text-center p-4 rounded-lg" style={{ backgroundColor: `${theme.background_tertiary}50` }}>
                          <p className="text-xs uppercase tracking-wider mb-2" style={{ color: theme.text_secondary }}>Average Mood Score</p>
                          <p className="text-4xl font-bold" style={{ color: theme.accent_color }}>{analyticsData?.mood?.average}</p>
                          <p className="text-sm mt-1" style={{ color: `${theme.text_secondary}60` }}>out of 5</p>
                        </div>
                        
                        {/* Mood Distribution */}
                        {Object.keys(analyticsData?.mood?.distribution || {}).length > 0 && (
                          <div>
                            <p className="text-xs uppercase tracking-wider mb-3" style={{ color: theme.text_secondary }}>Distribution</p>
                            <div className="flex items-end gap-1 h-16">
                              {[1, 2, 3, 4, 5].map(level => {
                                const count = analyticsData?.mood?.distribution?.[String(level)] || 0;
                                const maxCount = Math.max(...Object.values(analyticsData?.mood?.distribution || {}), 1);
                                const height = (count / maxCount) * 100;
                                const moodColors = [theme.error_color, theme.warning_color, '#facc15', '#84cc16', theme.success_color];
                                return (
                                  <div key={level} className="flex-1 flex flex-col items-center gap-1">
                                    <span className="text-xs" style={{ color: theme.text_secondary }}>{count}</span>
                                    <div 
                                      className="w-full rounded-t transition-all"
                                      style={{ 
                                        height: `${Math.max(height, 8)}%`,
                                        backgroundColor: moodColors[level - 1],
                                        minHeight: '4px'
                                      }}
                                    />
                                    <span className="text-xs" style={{ color: `${theme.text_secondary}60` }}>{level}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Smile className="w-12 h-12 mx-auto mb-3" style={{ color: `${theme.text_secondary}30` }} />
                        <p className="text-sm" style={{ color: `${theme.text_secondary}60` }}>
                          No mood data recorded yet
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Mood Trend Over Time */}
                {analyticsData?.mood?.trend && analyticsData.mood.trend.some(d => d.average !== null) && (
                  <div 
                    className="rounded-xl p-6"
                    style={{
                      background: theme.background_secondary,
                      border: `1px solid ${theme.border_color}`
                    }}
                    data-testid="mood-trend"
                  >
                    <h3 className="text-md font-semibold mb-4 flex items-center gap-2" style={{ color: theme.text_primary }}>
                      <Smile className="w-4 h-4" style={{ color: theme.accent_color }} />
                      Weekly Mood Trend (Group Average)
                    </h3>
                    <div className="flex items-end gap-2 h-32">
                      {analyticsData.mood.trend.map((day, idx) => {
                        const moodVal = day.average;
                        const height = moodVal ? (moodVal / 5) * 100 : 0;
                        const moodColors = {
                          1: theme.error_color,
                          2: theme.warning_color,
                          3: '#facc15',
                          4: '#84cc16',
                          5: theme.success_color
                        };
                        const color = moodVal ? moodColors[Math.round(moodVal)] || theme.accent_color : `${theme.text_secondary}30`;
                        return (
                          <div key={day.day || `mood-${idx}`} className="flex-1 flex flex-col items-center gap-1">
                            {moodVal && (
                              <span className="text-xs font-medium" style={{ color: theme.text_primary }}>{moodVal}</span>
                            )}
                            <div 
                              className="w-full rounded-t transition-all"
                              style={{ 
                                height: `${Math.max(height, 5)}%`,
                                backgroundColor: color,
                                minHeight: moodVal ? '10px' : '4px',
                                opacity: moodVal ? 1 : 0.3
                              }}
                            />
                            <span className="text-xs" style={{ color: theme.text_secondary }}>{day.day}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Individual User Mood Averages */}
                {analyticsData?.mood?.individual_users?.length > 0 && (
                  <div 
                    className="rounded-xl p-6"
                    style={{
                      background: theme.background_secondary,
                      border: `1px solid ${theme.border_color}`
                    }}
                    data-testid="individual-moods"
                  >
                    <h3 className="text-md font-semibold mb-4 flex items-center gap-2" style={{ color: theme.text_primary }}>
                      <Users className="w-4 h-4" style={{ color: theme.primary_color }} />
                      Individual User Mood Averages
                    </h3>
                    <div className="space-y-3">
                      {analyticsData.mood.individual_users.map((user, idx) => {
                        const moodVal = user.average_mood;
                        const topMood = user.top_mood;
                        const moodColors = {
                          1: theme.error_color,
                          2: theme.warning_color,
                          3: '#facc15',
                          4: '#84cc16',
                          5: theme.success_color
                        };
                        const color = moodColors[Math.round(moodVal)] || theme.accent_color;
                        return (
                          <div 
                            key={user.user_id || user.email || `user-${idx}`}
                            className="flex items-center justify-between p-3 rounded-lg"
                            style={{ backgroundColor: `${theme.background_tertiary}50` }}
                          >
                            <div className="flex items-center gap-3">
                              <div 
                                className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold"
                                style={{ 
                                  background: theme.primary_color,
                                  color: theme.text_on_brand || '#ffffff'
                                }}
                              >
                                {user.name?.charAt(0) || '?'}
                              </div>
                              <div>
                                <p className="font-medium" style={{ color: theme.text_primary }}>{user.name}</p>
                                <p className="text-xs" style={{ color: theme.text_secondary }}>
                                  {user.entry_count} entries · Top: {getMoodLabel(topMood)}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span 
                                className="text-lg font-bold"
                                style={{ color }}
                              >
                                {typeof moodVal === 'number' ? moodVal.toFixed(1) : moodVal}
                              </span>
                              <span className="text-xs" style={{ color: `${theme.text_secondary}60` }}>/5</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* AI Mood Insights */}
                {analyticsData?.mood_insights?.length > 0 && (
                  <div 
                    className="rounded-xl p-6"
                    style={{
                      background: theme.background_secondary,
                      border: `1px solid ${theme.primary_color}40`
                    }}
                    data-testid="mood-insights"
                  >
                    <h3 className="text-md font-semibold mb-4 flex items-center gap-2" style={{ color: theme.text_primary }}>
                      <Brain className="w-4 h-4" style={{ color: theme.primary_color }} />
                      AI Mood Insights
                      <span 
                        className="text-xs px-2 py-0.5 rounded-full"
                        style={{ 
                          background: theme.primary_color,
                          color: theme.text_on_brand || '#ffffff'
                        }}
                      >
                        Coach
                      </span>
                    </h3>
                    <div className="space-y-3">
                      {analyticsData.mood_insights.map((insight, idx) => {
                        const iconColors = {
                          positive: theme.success_color,
                          warning: theme.warning_color,
                          attention: theme.error_color,
                          info: theme.accent_color
                        };
                        const bgColors = {
                          positive: `${theme.success_color}15`,
                          warning: `${theme.warning_color}15`,
                          attention: `${theme.error_color}15`,
                          info: `${theme.accent_color}15`
                        };
                        const iconColor = iconColors[insight.type] || theme.accent_color;
                        const bgColor = bgColors[insight.type] || `${theme.accent_color}15`;
                        
                        return (
                          <div 
                            key={insight.title || `insight-${idx}`}
                            className="p-4 rounded-lg"
                            style={{ backgroundColor: bgColor }}
                          >
                            <div className="flex items-start gap-3">
                              <div 
                                className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                                style={{ backgroundColor: `${iconColor}30` }}
                              >
                                {insight.icon === 'trophy' && <Trophy className="w-4 h-4" style={{ color: iconColor }} />}
                                {insight.icon === 'alert' && <Bell className="w-4 h-4" style={{ color: iconColor }} />}
                                {insight.icon === 'trending_up' && <Sparkles className="w-4 h-4" style={{ color: iconColor }} />}
                                {insight.icon === 'trending_down' && <Moon className="w-4 h-4" style={{ color: iconColor }} />}
                                {insight.icon === 'users' && <Users className="w-4 h-4" style={{ color: iconColor }} />}
                                {insight.icon === 'clipboard' && <FileText className="w-4 h-4" style={{ color: iconColor }} />}
                              </div>
                              <div className="flex-1">
                                <h4 className="font-medium mb-1" style={{ color: theme.text_primary }}>
                                  {insight.title}
                                </h4>
                                <p className="text-sm" style={{ color: `${theme.text_secondary}CC` }}>
                                  {insight.message}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Leaderboard */}
                <div 
                  className="rounded-xl p-6"
                  style={{
                    background: theme.background_secondary,
                    border: `1px solid ${theme.border_color}`
                  }}
                  data-testid="leaderboard"
                >
                  <h3 className="text-md font-semibold mb-4 flex items-center gap-2" style={{ color: theme.text_primary }}>
                    <Flame className="w-4 h-4" style={{ color: theme.warning_color }} />
                    Top Journalers (by Streak)
                  </h3>
                  {analyticsData?.leaderboard?.length > 0 ? (
                    <div className="space-y-3">
                      {analyticsData.leaderboard.map((user, idx) => (
                        <div 
                          key={user.user_id || user.email || `leaderboard-${idx}`}
                          className="flex items-center justify-between p-3 rounded-lg"
                          style={{ backgroundColor: `${theme.background_tertiary}50` }}
                        >
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold"
                              style={{ 
                                backgroundColor: idx === 0 ? '#fbbf24' : idx === 1 ? '#94a3b8' : idx === 2 ? '#cd7f32' : `${theme.primary_color}30`,
                                color: idx < 3 ? '#0f172a' : theme.primary_color
                              }}
                            >
                              {idx + 1}
                            </div>
                            <div>
                              <p className="text-sm font-medium" style={{ color: theme.text_primary }}>{user.name}</p>
                              <p className="text-xs" style={{ color: theme.text_secondary }}>{user.email}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4 text-right">
                            <div>
                              <p className="text-lg font-bold flex items-center gap-1" style={{ color: theme.warning_color }}>
                                <Flame className="w-4 h-4" />
                                {user.streak}
                              </p>
                              <p className="text-xs" style={{ color: `${theme.text_secondary}60` }}>day streak</p>
                            </div>
                            <div>
                              <p className="text-lg font-bold" style={{ color: theme.primary_color }}>{user.entries}</p>
                              <p className="text-xs" style={{ color: `${theme.text_secondary}60` }}>entries</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Users className="w-12 h-12 mx-auto mb-3" style={{ color: `${theme.text_secondary}30` }} />
                      <p className="text-sm" style={{ color: `${theme.text_secondary}60` }}>
                        No user activity yet. Invite users to start tracking.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ==================== BANNERS TAB ==================== */}
        {activeTab === 'banners' && (
          <div data-testid="banners-tab-content">
            {/* Check if banner feature is enabled */}
            {dashboard?.client?.features?.main_promo_banner === false && dashboard?.client?.features?.sidebar_promo === false ? (
              /* Locked State */
              <div 
                className="rounded-xl p-8 text-center"
                style={{
                  background: theme.background_secondary,
                  border: `1px solid ${theme.border_color}`
                }}
              >
                <div 
                  className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center"
                  style={{ backgroundColor: `${theme.warning_color}20` }}
                >
                  <Lock className="w-10 h-10" style={{ color: theme.warning_color }} />
                </div>
                <h3 className="text-xl font-bold mb-3" style={{ color: theme.text_primary }}>
                  Banner Management Locked
                </h3>
                <p className="text-sm mb-6 max-w-md mx-auto" style={{ color: theme.text_secondary }}>
                  Banner management is not included in your current plan. Upgrade to create custom promotional banners for your clients.
                </p>
                <button 
                  onClick={() => setShowUpgradeModal(true)}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all hover:opacity-90"
                  style={{
                    background: theme.primary_color,
                    color: theme.text_on_brand || '#ffffff'
                  }}
                >
                  <Crown className="w-5 h-5" />
                  Upgrade to Unlock
                </button>
              </div>
            ) : (
              /* Active State - Use the new B2BBannerManager component */
              <B2BBannerManager key="b2b-banner-manager" theme={theme} />
            )}
          </div>
        )}

        {/* ==================== BRANDING TAB ==================== */}
        {activeTab === 'branding' && dashboard?.client?.features?.custom_themes !== false && (
          <div data-testid="branding-tab-content">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold flex items-center gap-2" style={{ color: theme.text_primary }}>
                <Palette className="w-5 h-5" style={{ color: theme.accent_color }} />
                Brand Customization
              </h2>
            </div>

            {/* Check if theme customization is enabled */}
            {dashboard?.client?.features?.themes === false ? (
              /* Locked State */
              <div 
                className="rounded-xl p-8 text-center"
                style={{
                  background: theme.background_secondary,
                  border: `1px solid ${theme.border_color}`
                }}
              >
                <div 
                  className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center"
                  style={{ backgroundColor: `${theme.warning_color}20` }}
                >
                  <Lock className="w-10 h-10" style={{ color: theme.warning_color }} />
                </div>
                <h3 className="text-xl font-bold mb-3" style={{ color: theme.text_primary }}>
                  Brand Customization Locked
                </h3>
                <p className="text-sm mb-6 max-w-md mx-auto" style={{ color: theme.text_secondary }}>
                  Theme customization is not included in your current plan. Upgrade to customize your portal's colors and branding.
                </p>
                <button 
                  onClick={() => setShowUpgradeModal(true)}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all hover:opacity-90"
                  style={{
                    background: theme.primary_color,
                    color: theme.text_on_brand || '#ffffff'
                  }}
                >
                  <Crown className="w-5 h-5" />
                  Upgrade to Unlock
                </button>
              </div>
            ) : (
              /* Active State */
              <div className="space-y-6">
                {/* Live Preview - Shows all 8 colors in action */}
                <div 
                  className="rounded-xl p-6"
                  style={{
                    background: brandingForm.card_color,
                    border: `1px solid ${brandingForm.border_color}`
                  }}
                >
                  <h3 className="text-md font-semibold mb-4" style={{ color: brandingForm.text_color }}>
                    Live Preview
                  </h3>
                  <div className="flex items-center gap-4 mb-4">
                    <div 
                      className="w-12 h-12 rounded-lg flex items-center justify-center"
                      style={{ background: brandingForm.brand_color }}
                    >
                      <Building2 className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold" style={{ color: brandingForm.text_color }}>Your Portal</p>
                      <p className="text-sm" style={{ color: brandingForm.secondary_text_color }}>Preview your brand colors</p>
                    </div>
                    <span 
                      className="ml-auto px-2 py-1 rounded text-xs font-medium"
                      style={{ background: brandingForm.accent_color, color: brandingForm.background_color }}
                    >
                      Accent Badge
                    </span>
                  </div>
                  {/* Sample input field */}
                  <div className="mb-4">
                    <input 
                      type="text"
                      placeholder="Sample input field..."
                      className="w-full px-3 py-2 rounded-lg text-sm"
                      style={{
                        background: brandingForm.input_background,
                        border: `1px solid ${brandingForm.border_color}`,
                        color: brandingForm.text_color
                      }}
                      readOnly
                    />
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <button 
                      className="px-4 py-2 rounded-lg text-sm font-medium"
                      style={{ 
                        background: brandingForm.brand_color,
                        color: brandingForm.background_color
                      }}
                    >
                      Primary Button
                    </button>
                    <button 
                      className="px-4 py-2 rounded-lg text-sm font-medium"
                      style={{ 
                        border: `1px solid ${brandingForm.brand_color}`,
                        color: brandingForm.brand_color,
                        backgroundColor: 'transparent'
                      }}
                    >
                      Outline
                    </button>
                    <button 
                      className="px-4 py-2 rounded-lg text-sm font-medium"
                      style={{ 
                        background: brandingForm.accent_color,
                        color: brandingForm.background_color
                      }}
                    >
                      Accent
                    </button>
                  </div>
                </div>

                {/* Color Settings - EXPANDED 8-COLOR SYSTEM */}
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Brand & Text Colors */}
                  <div 
                    className="rounded-xl p-6"
                    style={{
                      background: theme.background_secondary,
                      border: `1px solid ${theme.border_color}`
                    }}
                  >
                    <h3 className="text-md font-semibold mb-4" style={{ color: theme.text_primary }}>
                      Brand & Text
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm mb-2" style={{ color: theme.text_secondary }}>
                          Brand Color
                          <span className="text-xs ml-2 opacity-60">(buttons, links)</span>
                        </label>
                        <div className="flex items-center gap-3">
                          <input 
                            type="color" 
                            value={brandingForm.brand_color}
                            onChange={(e) => setBrandingForm({...brandingForm, brand_color: e.target.value})}
                            className="w-12 h-10 rounded cursor-pointer border-0"
                          />
                          <Input
                            value={brandingForm.brand_color}
                            onChange={(e) => setBrandingForm({...brandingForm, brand_color: e.target.value})}
                            className="flex-1"
                            style={{
                              backgroundColor: `${theme.background_tertiary}4D`,
                              borderColor: theme.border_color,
                              color: theme.text_primary
                            }}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm mb-2" style={{ color: theme.text_secondary }}>
                          Accent Color
                          <span className="text-xs ml-2 opacity-60">(highlights, badges)</span>
                        </label>
                        <div className="flex items-center gap-3">
                          <input 
                            type="color" 
                            value={brandingForm.accent_color}
                            onChange={(e) => setBrandingForm({...brandingForm, accent_color: e.target.value})}
                            className="w-12 h-10 rounded cursor-pointer border-0"
                          />
                          <Input
                            value={brandingForm.accent_color}
                            onChange={(e) => setBrandingForm({...brandingForm, accent_color: e.target.value})}
                            className="flex-1"
                            style={{
                              backgroundColor: `${theme.background_tertiary}4D`,
                              borderColor: theme.border_color,
                              color: theme.text_primary
                            }}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm mb-2" style={{ color: theme.text_secondary }}>
                          Text Color
                          <span className="text-xs ml-2 opacity-60">(primary text)</span>
                        </label>
                        <div className="flex items-center gap-3">
                          <input 
                            type="color" 
                            value={brandingForm.text_color}
                            onChange={(e) => setBrandingForm({...brandingForm, text_color: e.target.value})}
                            className="w-12 h-10 rounded cursor-pointer border-0"
                          />
                          <Input
                            value={brandingForm.text_color}
                            onChange={(e) => setBrandingForm({...brandingForm, text_color: e.target.value})}
                            className="flex-1"
                            style={{
                              backgroundColor: `${theme.background_tertiary}4D`,
                              borderColor: theme.border_color,
                              color: theme.text_primary
                            }}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm mb-2" style={{ color: theme.text_secondary }}>
                          Secondary Text
                          <span className="text-xs ml-2 opacity-60">(muted, helper text)</span>
                        </label>
                        <div className="flex items-center gap-3">
                          <input 
                            type="color" 
                            value={brandingForm.secondary_text_color}
                            onChange={(e) => setBrandingForm({...brandingForm, secondary_text_color: e.target.value})}
                            className="w-12 h-10 rounded cursor-pointer border-0"
                          />
                          <Input
                            value={brandingForm.secondary_text_color}
                            onChange={(e) => setBrandingForm({...brandingForm, secondary_text_color: e.target.value})}
                            className="flex-1"
                            style={{
                              backgroundColor: `${theme.background_tertiary}4D`,
                              borderColor: theme.border_color,
                              color: theme.text_primary
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Background Colors */}
                  <div 
                    className="rounded-xl p-6"
                    style={{
                      background: theme.background_secondary,
                      border: `1px solid ${theme.border_color}`
                    }}
                  >
                    <h3 className="text-md font-semibold mb-4" style={{ color: theme.text_primary }}>
                      Backgrounds & Borders
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm mb-2" style={{ color: theme.text_secondary }}>
                          Background Color
                          <span className="text-xs ml-2 opacity-60">(main page)</span>
                        </label>
                        <div className="flex items-center gap-3">
                          <input 
                            type="color" 
                            value={brandingForm.background_color}
                            onChange={(e) => setBrandingForm({...brandingForm, background_color: e.target.value})}
                            className="w-12 h-10 rounded cursor-pointer border-0"
                          />
                          <Input
                            value={brandingForm.background_color}
                            onChange={(e) => setBrandingForm({...brandingForm, background_color: e.target.value})}
                            className="flex-1"
                            style={{
                              backgroundColor: `${theme.background_tertiary}4D`,
                              borderColor: theme.border_color,
                              color: theme.text_primary
                            }}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm mb-2" style={{ color: theme.text_secondary }}>
                          Card Color
                          <span className="text-xs ml-2 opacity-60">(panels, sections)</span>
                        </label>
                        <div className="flex items-center gap-3">
                          <input 
                            type="color" 
                            value={brandingForm.card_color}
                            onChange={(e) => setBrandingForm({...brandingForm, card_color: e.target.value})}
                            className="w-12 h-10 rounded cursor-pointer border-0"
                          />
                          <Input
                            value={brandingForm.card_color}
                            onChange={(e) => setBrandingForm({...brandingForm, card_color: e.target.value})}
                            className="flex-1"
                            style={{
                              backgroundColor: `${theme.background_tertiary}4D`,
                              borderColor: theme.border_color,
                              color: theme.text_primary
                            }}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm mb-2" style={{ color: theme.text_secondary }}>
                          Border Color
                          <span className="text-xs ml-2 opacity-60">(card borders, dividers)</span>
                        </label>
                        <div className="flex items-center gap-3">
                          <input 
                            type="color" 
                            value={brandingForm.border_color}
                            onChange={(e) => setBrandingForm({...brandingForm, border_color: e.target.value})}
                            className="w-12 h-10 rounded cursor-pointer border-0"
                          />
                          <Input
                            value={brandingForm.border_color}
                            onChange={(e) => setBrandingForm({...brandingForm, border_color: e.target.value})}
                            className="flex-1"
                            style={{
                              backgroundColor: `${theme.background_tertiary}4D`,
                              borderColor: theme.border_color,
                              color: theme.text_primary
                            }}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm mb-2" style={{ color: theme.text_secondary }}>
                          Input Background
                          <span className="text-xs ml-2 opacity-60">(form fields)</span>
                        </label>
                        <div className="flex items-center gap-3">
                          <input 
                            type="color" 
                            value={brandingForm.input_background}
                            onChange={(e) => setBrandingForm({...brandingForm, input_background: e.target.value})}
                            className="w-12 h-10 rounded cursor-pointer border-0"
                          />
                          <Input
                            value={brandingForm.input_background}
                            onChange={(e) => setBrandingForm({...brandingForm, input_background: e.target.value})}
                            className="flex-1"
                            style={{
                              backgroundColor: `${theme.background_tertiary}4D`,
                              borderColor: theme.border_color,
                              color: theme.text_primary
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end gap-3">
                  <Button
                    variant="outline"
                    onClick={() => {
                      // Reset to the original admin-set theme, not current dashboard values
                      const adminTheme = originalAdminTheme || dashboard?.client?.theme || {};
                      setBrandingForm({
                        brand_color: adminTheme.brand_color || adminTheme.primary_color || '#E91E63',
                        background_color: adminTheme.background_color || adminTheme.background_primary || '#0F172A',
                        card_color: adminTheme.card_color || adminTheme.background_secondary || '#1E293B',
                        text_color: adminTheme.text_color || adminTheme.text_primary || '#F8FAFC',
                        border_color: adminTheme.border_color || '#475569',
                        accent_color: adminTheme.accent_color || adminTheme.secondary_color || '#9C27B0',
                        secondary_text_color: adminTheme.secondary_text_color || adminTheme.text_secondary || '#94A3B8',
                        input_background: adminTheme.input_background || adminTheme.card_color || adminTheme.background_secondary || '#1E293B'
                      });
                      toast.success('Reset to admin-configured colors');
                    }}
                    style={{
                      borderColor: theme.border_color,
                      color: theme.text_secondary
                    }}
                  >
                    Reset
                  </Button>
                  <Button
                    onClick={handleSaveBranding}
                    disabled={savingBranding}
                    style={{
                      background: theme.primary_color,
                      color: theme.text_on_brand || '#ffffff'
                    }}
                    data-testid="save-branding-btn"
                  >
                    {savingBranding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Save Branding
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ==================== VIDEOS TAB ==================== */}
        {activeTab === 'videos' && dashboard?.client?.features?.video_embeds !== false && (
          <div data-testid="videos-tab-content">
            <VideoEmbedsManagerUnified 
              theme={theme}
              isAdmin={false}
              isPreviewMode={isPreviewMode}
              showTierSelector={false}
              showAnalytics={false}
              isB2B={true}
            />
          </div>
        )}

        {/* User Detail Modal */}
        {showUserDetailModal && selectedUser && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div 
              className="rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
              style={{
                background: theme.background_secondary, // SOLID color,
                border: `1px solid ${theme.border_color}`
              }}
            >
              <div className="p-6" style={{ borderBottom: `1px solid ${theme.border_color}` }}>
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold" style={{ color: theme.text_primary }}>User Details</h3>
                  <div className="flex items-center gap-2">
                    {!isEditingUser && !isPreviewMode && selectedUser.status === 'activated' && (
                      <button 
                        onClick={() => {
                          setEditUserForm({
                            first_name: selectedUser.first_name || '',
                            last_name: selectedUser.last_name || ''
                          });
                          setIsEditingUser(true);
                        }}
                        className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                        style={{ color: theme.accent_color }}
                        title="Edit User"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                    )}
                    <button 
                      onClick={() => { 
                        setShowUserDetailModal(false); 
                        setSelectedUser(null); 
                        setUserStats(null); 
                        setIsEditingUser(false);
                      }} 
                      style={{ color: theme.text_secondary }}
                      className="hover:opacity-100"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
              <div className="p-6">
                {/* User Info */}
                <div className="flex items-center gap-4 mb-6">
                  <div 
                    className="w-14 h-14 rounded-full flex items-center justify-center text-xl font-bold"
                    style={{ 
                      background: theme.primary_color,
                      color: theme.text_primary
                    }}
                  >
                    {(isEditingUser ? editUserForm.first_name : selectedUser.first_name)?.charAt(0) || selectedUser.email?.charAt(0) || '?'}
                  </div>
                  <div className="flex-1">
                    {isEditingUser ? (
                      <div className="space-y-2">
                        <input
                          type="text"
                          value={editUserForm.first_name}
                          onChange={(e) => setEditUserForm(prev => ({ ...prev, first_name: e.target.value }))}
                          placeholder="First Name"
                          className="w-full px-3 py-2 rounded-lg text-sm"
                          style={{ 
                            backgroundColor: theme.background_primary,
                            border: `1px solid ${theme.border_color}`,
                            color: theme.text_primary
                          }}
                        />
                        <input
                          type="text"
                          value={editUserForm.last_name}
                          onChange={(e) => setEditUserForm(prev => ({ ...prev, last_name: e.target.value }))}
                          placeholder="Last Name"
                          className="w-full px-3 py-2 rounded-lg text-sm"
                          style={{ 
                            backgroundColor: theme.background_primary,
                            border: `1px solid ${theme.border_color}`,
                            color: theme.text_primary
                          }}
                        />
                        <div className="flex gap-2 mt-2">
                          <button
                            onClick={handleUpdateUser}
                            disabled={savingUserEdit || !editUserForm.first_name.trim()}
                            className="px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1"
                            style={{ 
                              backgroundColor: theme.accent_color,
                              color: 'white',
                              opacity: (savingUserEdit || !editUserForm.first_name.trim()) ? 0.5 : 1
                            }}
                          >
                            {savingUserEdit ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                            Save
                          </button>
                          <button
                            onClick={() => setIsEditingUser(false)}
                            className="px-3 py-1.5 rounded-lg text-sm"
                            style={{ 
                              border: `1px solid ${theme.border_color}`,
                              color: theme.text_secondary
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <h4 className="font-bold text-lg" style={{ color: theme.text_primary }}>
                          {selectedUser.first_name ? `${selectedUser.first_name} ${selectedUser.last_name || ''}` : 'Unnamed User'}
                        </h4>
                        <p className="text-sm" style={{ color: theme.text_secondary }}>{selectedUser.email}</p>
                        <span 
                          className="text-xs px-2 py-0.5 rounded-full mt-1 inline-block"
                          style={{
                            backgroundColor: selectedUser.status === 'activated' ? `${theme.success_color}33` : `${theme.warning_color}33`,
                            color: selectedUser.status === 'activated' ? theme.success_color : theme.warning_color
                          }}
                        >
                          {selectedUser.status === 'activated' ? 'Activated' : 'Pending'}
                        </span>
                      </>
                    )}
                  </div>
                </div>

                {/* Stats Grid */}
                {loadingUserStats ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin" style={{ color: theme.accent_color }} />
                  </div>
                ) : selectedUser.status === 'pending' ? (
                  <div 
                    className="rounded-xl p-6 text-center"
                    style={{ backgroundColor: `${theme.warning_color}15`, border: `1px solid ${theme.warning_color}30` }}
                  >
                    <UserX className="w-10 h-10 mx-auto mb-3" style={{ color: theme.warning_color }} />
                    <p style={{ color: theme.warning_color }}>User hasn't activated yet</p>
                    <p className="text-sm mt-2" style={{ color: theme.text_secondary }}>
                      Invited on {selectedUser.invited_at ? format(new Date(selectedUser.invited_at), 'MMMM d, yyyy') : 'Unknown'}
                    </p>
                    {!isPreviewMode && (
                      <Button
                        onClick={() => { handleResendInvite(selectedUser.license_user_id); }}
                        className="mt-4"
                        style={{
                          background: theme.primary_color,
                          color: theme.text_primary
                        }}
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Resend Invitation
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Activity Stats */}
                    <div className="grid grid-cols-3 gap-3">
                      <div 
                        className="rounded-xl p-4 text-center"
                        style={{ backgroundColor: `${theme.background_tertiary}50`, border: `1px solid ${theme.border_color}30` }}
                      >
                        <FileText className="w-5 h-5 mx-auto mb-2" style={{ color: theme.primary_color }} />
                        <p className="text-xl font-bold" style={{ color: theme.text_primary }}>{userStats?.total_entries || 0}</p>
                        <p className="text-xs" style={{ color: theme.text_secondary }}>Total Entries</p>
                      </div>
                      <div 
                        className="rounded-xl p-4 text-center"
                        style={{ backgroundColor: `${theme.background_tertiary}50`, border: `1px solid ${theme.border_color}30` }}
                      >
                        <Flame className="w-5 h-5 mx-auto mb-2" style={{ color: theme.warning_color }} />
                        <p className="text-xl font-bold" style={{ color: theme.text_primary }}>{userStats?.current_streak || 0}</p>
                        <p className="text-xs" style={{ color: theme.text_secondary }}>Current Streak</p>
                      </div>
                      <div 
                        className="rounded-xl p-4 text-center"
                        style={{ backgroundColor: `${theme.background_tertiary}50`, border: `1px solid ${theme.border_color}30` }}
                      >
                        <Award className="w-5 h-5 mx-auto mb-2" style={{ color: theme.accent_color }} />
                        <p className="text-xl font-bold" style={{ color: theme.text_primary }}>{userStats?.badges_earned || 0}</p>
                        <p className="text-xs" style={{ color: theme.text_secondary }}>Badges</p>
                      </div>
                    </div>

                    {/* Additional Info */}
                    <div 
                      className="rounded-xl p-4 space-y-3"
                      style={{ backgroundColor: `${theme.background_tertiary}30`, border: `1px solid ${theme.border_color}30` }}
                    >
                      <div className="flex justify-between items-center">
                        <span className="text-sm" style={{ color: theme.text_secondary }}>Longest Streak</span>
                        <span className="font-medium" style={{ color: theme.text_primary }}>{userStats?.longest_streak || 0} days</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm" style={{ color: theme.text_secondary }}>Avg Mood</span>
                        <span className="font-medium" style={{ color: theme.text_primary }}>
                          {userStats?.mood_average ? `${userStats.mood_average}/5` : 'N/A'}
                        </span>
                      </div>
                      {userStats?.most_common_mood && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm" style={{ color: theme.text_secondary }}>Top Mood</span>
                          <span className="font-medium capitalize" style={{ color: theme.text_primary }}>
                            {getMoodLabel(userStats.most_common_mood)}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between items-center">
                        <span className="text-sm" style={{ color: theme.text_secondary }}>Last Entry</span>
                        <span className="font-medium" style={{ color: theme.text_primary }}>
                          {userStats?.last_entry_date ? format(new Date(userStats.last_entry_date), 'MMM d, yyyy') : 'Never'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm" style={{ color: theme.text_secondary }}>Activated</span>
                        <span className="font-medium" style={{ color: theme.text_primary }}>
                          {selectedUser.activated_at ? format(new Date(selectedUser.activated_at), 'MMM d, yyyy') : 'Unknown'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Invite User Modal */}
        {showInviteModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div 
              className="rounded-xl w-full max-w-md"
              style={{
                background: theme.background_secondary, // SOLID color,
                border: `1px solid ${theme.border_color}`
              }}
            >
              <div className="p-6" style={{ borderBottom: `1px solid ${theme.border_color}` }}>
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold" style={{ color: theme.text_primary }}>Invite User</h3>
                  <button 
                    onClick={() => setShowInviteModal(false)} 
                    style={{ color: theme.text_secondary }}
                    className="hover:opacity-100"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <form onSubmit={handleInviteUser} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm mb-1" style={{ color: theme.text_secondary }}>Email *</label>
                  <Input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="user@example.com"
                    required
                    style={{
                      backgroundColor: `${theme.background_tertiary}4D`,
                      borderColor: theme.border_color,
                      color: theme.text_primary
                    }}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm mb-1" style={{ color: theme.text_secondary }}>First Name *</label>
                    <Input
                      value={inviteFirstName}
                      onChange={(e) => setInviteFirstName(e.target.value)}
                      placeholder="John"
                      required
                      style={{
                        backgroundColor: `${theme.background_tertiary}4D`,
                        borderColor: theme.border_color,
                        color: theme.text_primary
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-1" style={{ color: theme.text_secondary }}>Last Name *</label>
                    <Input
                      value={inviteLastName}
                      onChange={(e) => setInviteLastName(e.target.value)}
                      placeholder="Doe"
                      required
                      style={{
                        backgroundColor: `${theme.background_tertiary}4D`,
                        borderColor: theme.border_color,
                        color: theme.text_primary
                      }}
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setShowInviteModal(false)}
                    style={{
                      borderColor: theme.border_color,
                      color: theme.text_secondary
                    }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit"
                    style={{
                      background: theme.primary_color,
                      color: theme.text_on_brand || '#ffffff'
                    }}
                    disabled={!inviteEmail.trim() || !inviteFirstName.trim() || !inviteLastName.trim()}
                  >
                    Send Invitation
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Bulk Upload Modal */}
        {showBulkModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div 
              className="rounded-xl w-full max-w-xl"
              style={{
                background: theme.background_secondary, // SOLID color,
                border: `1px solid ${theme.border_color}`
              }}
            >
              <div className="p-6" style={{ borderBottom: `1px solid ${theme.border_color}` }}>
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold" style={{ color: theme.text_primary }}>Upload Users CSV</h3>
                  <button 
                    onClick={() => { setShowBulkModal(false); setBulkUsers([]); }} 
                    style={{ color: theme.text_secondary }}
                    className="hover:opacity-100"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <p className="text-sm" style={{ color: theme.text_secondary }}>
                  Upload a CSV file with columns: <code className="px-1 rounded" style={{ backgroundColor: `${theme.background_tertiary}4D` }}>email</code>, 
                  <code className="px-1 rounded ml-1" style={{ backgroundColor: `${theme.background_tertiary}4D` }}>first_name</code>, <code className="px-1 rounded ml-1" style={{ backgroundColor: `${theme.background_tertiary}4D` }}>last_name</code> (all required)
                </p>
                <p className="text-sm" style={{ color: theme.text_secondary }}>
                  Available seats: {stats?.remaining_seats || 0}
                </p>
                <div 
                  className="border-2 border-dashed rounded-lg p-6 text-center"
                  style={{ borderColor: theme.border_color }}
                >
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleCSVUpload}
                    className="hidden"
                    id="bulk-csv-upload"
                  />
                  <label htmlFor="bulk-csv-upload" className="cursor-pointer">
                    <Upload className="w-10 h-10 mx-auto mb-3" style={{ color: theme.text_secondary }} />
                    <p style={{ color: theme.text_secondary }}>Click to upload CSV</p>
                  </label>
                </div>
                {bulkUsers.length > 0 && (
                  <div className="rounded-lg p-4" style={{ backgroundColor: `${theme.card_color || theme.background_secondary}33` }}>
                    <p className="text-sm font-medium mb-2" style={{ color: theme.accent_color }}>
                      {bulkUsers.length} users ready to invite
                    </p>
                    <div className="max-h-32 overflow-y-auto text-xs space-y-1" style={{ color: theme.text_secondary }}>
                      {bulkUsers.slice(0, 10).map((user, i) => (
                        <div key={user.email || `bulk-${i}`}>{user.email} {user.first_name && `(${user.first_name})`}</div>
                      ))}
                      {bulkUsers.length > 10 && (
                        <div>...and {bulkUsers.length - 10} more</div>
                      )}
                    </div>
                  </div>
                )}
                <div className="flex justify-end gap-3 pt-4">
                  <Button 
                    variant="outline" 
                    onClick={() => { setShowBulkModal(false); setBulkUsers([]); }}
                    style={{
                      borderColor: theme.border_color,
                      color: theme.text_secondary
                    }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleBulkInvite}
                    disabled={bulkUsers.length === 0}
                    style={{
                      background: theme.primary_color,
                      color: theme.text_on_brand || '#ffffff'
                    }}
                  >
                    Send {bulkUsers.length} Invitations
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Promo Campaign Modal */}
        {showPromoModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div 
              className="rounded-xl w-full max-w-2xl my-8"
              style={{
                background: theme.background_secondary, // SOLID color,
                border: `1px solid ${theme.border_color}`
              }}
            >
              <div className="p-6" style={{ borderBottom: `1px solid ${theme.border_color}` }}>
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold" style={{ color: theme.text_primary }}>
                    {editingPromo ? 'Edit Campaign' : 'New Promo Campaign'}
                  </h3>
                  <button 
                    onClick={() => setShowPromoModal(false)} 
                    style={{ color: theme.text_secondary }}
                    className="hover:opacity-100"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                {/* Banner Type */}
                <div>
                  <label className="block text-sm mb-2" style={{ color: theme.text_secondary }}>Banner Type</label>
                  <div className="flex gap-2">
                    {['styled', 'image', 'video'].map((type) => (
                      <button
                        key={type}
                        onClick={() => setPromoForm({ ...promoForm, banner_type: type })}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg capitalize"
                        style={{
                          backgroundColor: promoForm.banner_type === type ? `${theme.accent_color}33` : `${theme.background_tertiary}4D`,
                          color: promoForm.banner_type === type ? theme.accent_color : theme.text_secondary,
                          border: `1px solid ${promoForm.banner_type === type ? theme.accent_color : theme.border_color}`
                        }}
                      >
                        {type === 'styled' && <Sparkles className="w-4 h-4" />}
                        {type === 'image' && <ImageIcon className="w-4 h-4" />}
                        {type === 'video' && <Video className="w-4 h-4" />}
                        {type}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm mb-1" style={{ color: theme.text_secondary }}>Title *</label>
                  <Input
                    value={promoForm.title}
                    onChange={(e) => setPromoForm({ ...promoForm, title: e.target.value })}
                    placeholder="Campaign title"
                    style={{
                      backgroundColor: `${theme.background_tertiary}4D`,
                      borderColor: theme.border_color,
                      color: theme.text_primary
                    }}
                  />
                </div>

                {/* Subtitle & Description */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm mb-1" style={{ color: theme.text_secondary }}>Subtitle</label>
                    <Input
                      value={promoForm.subtitle}
                      onChange={(e) => setPromoForm({ ...promoForm, subtitle: e.target.value })}
                      placeholder="Optional subtitle"
                      style={{
                        backgroundColor: `${theme.background_tertiary}4D`,
                        borderColor: theme.border_color,
                        color: theme.text_primary
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-1" style={{ color: theme.text_secondary }}>Discount Label</label>
                    <Input
                      value={promoForm.discount_label}
                      onChange={(e) => setPromoForm({ ...promoForm, discount_label: e.target.value })}
                      placeholder="e.g., 20% OFF"
                      style={{
                        backgroundColor: `${theme.background_tertiary}4D`,
                        borderColor: theme.border_color,
                        color: theme.text_primary
                      }}
                    />
                  </div>
                </div>

                {/* CTA */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm mb-1" style={{ color: theme.text_secondary }}>CTA Text</label>
                    <Input
                      value={promoForm.cta_text}
                      onChange={(e) => setPromoForm({ ...promoForm, cta_text: e.target.value })}
                      placeholder="Learn More"
                      style={{
                        backgroundColor: `${theme.background_tertiary}4D`,
                        borderColor: theme.border_color,
                        color: theme.text_primary
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-1" style={{ color: theme.text_secondary }}>CTA Link</label>
                    <Input
                      value={promoForm.cta_link}
                      onChange={(e) => setPromoForm({ ...promoForm, cta_link: e.target.value })}
                      placeholder="https://..."
                      style={{
                        backgroundColor: `${theme.background_tertiary}4D`,
                        borderColor: theme.border_color,
                        color: theme.text_primary
                      }}
                    />
                  </div>
                </div>

                {/* Styled Banner Colors */}
                {promoForm.banner_type === 'styled' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm mb-1" style={{ color: theme.text_secondary }}>Gradient From</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={promoForm.gradient_from}
                          onChange={(e) => setPromoForm({ ...promoForm, gradient_from: e.target.value })}
                          className="w-10 h-10 rounded cursor-pointer"
                        />
                        <Input
                          value={promoForm.gradient_from}
                          onChange={(e) => setPromoForm({ ...promoForm, gradient_from: e.target.value })}
                          className="flex-1"
                          style={{
                            backgroundColor: `${theme.background_tertiary}4D`,
                            borderColor: theme.border_color,
                            color: theme.text_primary
                          }}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm mb-1" style={{ color: theme.text_secondary }}>Gradient To</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={promoForm.gradient_to}
                          onChange={(e) => setPromoForm({ ...promoForm, gradient_to: e.target.value })}
                          className="w-10 h-10 rounded cursor-pointer"
                        />
                        <Input
                          value={promoForm.gradient_to}
                          onChange={(e) => setPromoForm({ ...promoForm, gradient_to: e.target.value })}
                          className="flex-1"
                          style={{
                            backgroundColor: `${theme.background_tertiary}4D`,
                            borderColor: theme.border_color,
                            color: theme.text_primary
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Image Upload */}
                {promoForm.banner_type === 'image' && (
                  <div>
                    <label className="block text-sm mb-1" style={{ color: theme.text_secondary }}>Banner Image</label>
                    <div className="flex items-center gap-4">
                      {promoForm.image_url ? (
                        <img src={promoForm.image_url} alt="Preview" className="w-24 h-16 rounded object-cover" />
                      ) : (
                        <div 
                          className="w-24 h-16 rounded flex items-center justify-center"
                          style={{ backgroundColor: `${theme.background_tertiary}4D` }}
                        >
                          <ImageIcon className="w-6 h-6" style={{ color: theme.text_secondary }} />
                        </div>
                      )}
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageUpload(e, 'image')}
                          className="hidden"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          disabled={uploading}
                          style={{ borderColor: theme.border_color, color: theme.text_secondary }}
                        >
                          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                          Upload Image
                        </Button>
                      </label>
                    </div>
                  </div>
                )}

                {/* Video Upload */}
                {promoForm.banner_type === 'video' && (
                  <div>
                    <label className="block text-sm mb-1" style={{ color: theme.text_secondary }}>Banner Video</label>
                    <div className="flex items-center gap-4">
                      {promoForm.video_url ? (
                        <video src={promoForm.video_url} className="w-32 h-20 rounded object-cover" />
                      ) : (
                        <div 
                          className="w-32 h-20 rounded flex items-center justify-center"
                          style={{ backgroundColor: `${theme.background_tertiary}4D` }}
                        >
                          <Video className="w-6 h-6" style={{ color: theme.text_secondary }} />
                        </div>
                      )}
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="video/*"
                          onChange={(e) => handleImageUpload(e, 'video')}
                          className="hidden"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          disabled={uploading}
                          style={{ borderColor: theme.border_color, color: theme.text_secondary }}
                        >
                          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                          Upload Video
                        </Button>
                      </label>
                    </div>
                  </div>
                )}

                {/* Active Toggle */}
                <div 
                  className="flex items-center justify-between p-3 rounded-lg cursor-pointer"
                  style={{ backgroundColor: `${theme.background_tertiary}4D` }}
                  onClick={() => setPromoForm({ ...promoForm, is_active: !promoForm.is_active })}
                >
                  <span style={{ color: theme.text_primary }}>Activate Campaign</span>
                  {promoForm.is_active ? (
                    <ToggleRight className="w-6 h-6" style={{ color: theme.success_color }} />
                  ) : (
                    <ToggleLeft className="w-6 h-6" style={{ color: theme.text_secondary }} />
                  )}
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowPromoModal(false)}
                    style={{
                      borderColor: theme.border_color,
                      color: theme.text_secondary
                    }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleSavePromo}
                    style={{
                      background: theme.primary_color,
                      color: theme.text_on_brand || '#ffffff'
                    }}
                  >
                    {editingPromo ? 'Update Campaign' : 'Create Campaign'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Notification Modal */}
        {/* ==================== NOTIFICATION MODAL - Pixel-perfect clone of AdminDashboard ==================== */}
        {showNotifModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div 
              className="rounded-2xl p-6 w-full max-w-2xl my-8 max-h-[90vh] overflow-y-auto"
              style={{
                background: `linear-gradient(to bottom right, ${theme.background_secondary}, ${theme.background_primary})`,
                border: `1px solid ${theme.border_color}`
              }}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold flex items-center gap-2" style={{ color: theme.text_primary }}>
                  <Send className="w-5 h-5" style={{ color: theme.accent_color }} />
                  {editingNotif ? 'Edit Notification' : 'Create Notification'}
                </h3>
                <button 
                  type="button"
                  onClick={() => setShowNotifModal(false)} 
                  style={{ color: `${theme.text_primary}80` }}
                  className="hover:opacity-100"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-5">
                {/* Image Upload - First like AdminDashboard */}
                <div>
                  <label className="block text-sm mb-1" style={{ color: `${theme.text_primary}B3` }}>
                    Image/Flyer (optional)
                  </label>
                  {notifImagePreview || notifForm.image_url ? (
                    <div className="relative">
                      <img 
                        src={notifImagePreview || (notifForm.image_url.startsWith('/api/') ? `${API_BASE}${notifForm.image_url}` : notifForm.image_url)}
                        alt="Preview" 
                        className="w-full max-h-48 object-contain rounded-lg"
                        style={{ backgroundColor: `${theme.background_primary}4D` }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setNotifForm({ ...notifForm, image_url: '' });
                          setNotifImagePreview(null);
                        }}
                        className="absolute top-2 right-2 p-2 rounded-full hover:opacity-80"
                        style={{ backgroundColor: `${theme.error_color}CC`, color: '#fff' }}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <label 
                      className="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed rounded-lg cursor-pointer transition-colors hover:opacity-80"
                      style={{ 
                        borderColor: theme.border_color,
                        backgroundColor: `${theme.border_color}1A`
                      }}
                    >
                      <Upload className="w-6 h-6 mb-1" style={{ color: `${theme.text_primary}66` }} />
                      <span className="text-sm" style={{ color: `${theme.text_primary}80` }}>Click to upload</span>
                      <input 
                        type="file" 
                        className="hidden" 
                        accept="image/*"
                        onChange={handleNotifImageUpload}
                      />
                    </label>
                  )}
                </div>

                {/* Title with Merge Tags indicator */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-sm" style={{ color: `${theme.text_primary}B3` }}>Title</label>
                    {notifForm.use_personalization && (
                      <span className="text-xs" style={{ color: theme.accent_color }}>Personalization ON</span>
                    )}
                  </div>
                  <Input
                    value={notifForm.title}
                    onChange={(e) => setNotifForm({...notifForm, title: e.target.value})}
                    placeholder="e.g., Hey {{first_name}}, check this out!"
                    style={{
                      backgroundColor: `${theme.border_color}4D`,
                      borderColor: theme.border_color,
                      color: theme.text_primary
                    }}
                  />
                </div>

                {/* Message with Merge Tags dropdown - Exact same select element style */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-sm" style={{ color: `${theme.text_primary}B3` }}>Message</label>
                    <div className="relative">
                      <select
                        onChange={(e) => { if (e.target.value) insertMergeTag(e.target.value); e.target.value = ''; }}
                        className="text-xs rounded px-2 py-1 cursor-pointer"
                        style={{
                          backgroundColor: `${theme.border_color}80`,
                          border: `1px solid ${theme.border_color}`,
                          color: theme.accent_color
                        }}
                      >
                        <option value="">+ Insert Tag</option>
                        <option value="{{first_name}}">First Name</option>
                        <option value="{{name}}">Full Name</option>
                        <option value="{{streak}}">Streak Count</option>
                        <option value="{{email}}">Email</option>
                      </select>
                    </div>
                  </div>
                  <textarea
                    value={notifForm.message}
                    onChange={(e) => setNotifForm({...notifForm, message: e.target.value})}
                    placeholder="Write your notification message... Use {{first_name}} for personalization"
                    rows={3}
                    className="w-full rounded-md px-3 py-2 focus:outline-none"
                    style={{
                      backgroundColor: `${theme.border_color}4D`,
                      border: `1px solid ${theme.border_color}`,
                      color: theme.text_primary
                    }}
                  />
                </div>

                {/* Two columns for Type and Show Popup - Exact layout match */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm mb-1" style={{ color: `${theme.text_primary}B3` }}>Type</label>
                    <select
                      value={notifForm.type}
                      onChange={(e) => setNotifForm({...notifForm, type: e.target.value})}
                      className="w-full rounded-md px-3 py-2 focus:outline-none"
                      style={{
                        backgroundColor: `${theme.border_color}4D`,
                        border: `1px solid ${theme.border_color}`,
                        color: theme.text_primary
                      }}
                    >
                      <option value="announcement">Announcement</option>
                      <option value="info">Info</option>
                      <option value="alert">Alert</option>
                      <option value="promotion">Promotion</option>
                    </select>
                  </div>
                  <div className="flex items-end pb-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notifForm.show_popup}
                        onChange={(e) => setNotifForm({...notifForm, show_popup: e.target.checked})}
                        className="w-4 h-4 rounded"
                        style={{ 
                          borderColor: theme.border_color,
                          backgroundColor: `${theme.border_color}4D`
                        }}
                      />
                      <span className="text-sm" style={{ color: `${theme.text_primary}B3` }}>Show as popup</span>
                    </label>
                  </div>
                </div>

                {/* Targeting Section - Boxed style matching AdminDashboard */}
                <div 
                  className="rounded-lg p-4"
                  style={{ 
                    backgroundColor: `${theme.border_color}33`,
                    border: `1px solid ${theme.border_color}80`
                  }}
                >
                  <h4 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: theme.text_primary }}>
                    <Users className="w-4 h-4" style={{ color: theme.accent_color }} />
                    Targeting
                  </h4>
                  <div className="space-y-3">
                    <div className="flex gap-2">
                      {['all', 'filter', 'specific'].map((type) => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => setNotifForm({...notifForm, target_type: type, target_user_ids: [], target_filter: ''})}
                          className="px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
                          style={{
                            backgroundColor: notifForm.target_type === type 
                              ? theme.accent_color
                              : `${theme.border_color}80`,
                            color: notifForm.target_type === type 
                              ? theme.background_primary
                              : `${theme.text_primary}B3`
                          }}
                        >
                          {type === 'all' ? 'All Users' : type === 'filter' ? 'By Filter' : 'Select Users'}
                        </button>
                      ))}
                    </div>

                    {notifForm.target_type === 'filter' && (
                      <select
                        value={notifForm.target_filter}
                        onChange={(e) => setNotifForm({...notifForm, target_filter: e.target.value})}
                        className="w-full rounded-md px-3 py-2 text-sm"
                        style={{
                          backgroundColor: `${theme.border_color}4D`,
                          border: `1px solid ${theme.border_color}`,
                          color: theme.text_primary
                        }}
                      >
                        <option value="">Select filter...</option>
                        <option value="active_last_7_days">Active in last 7 days</option>
                        <option value="inactive_7_days">Inactive for 7+ days</option>
                        <option value="new_this_week">Joined this week</option>
                        <option value="no_entries">No journal entries</option>
                        <option value="has_streak">Has active streak</option>
                      </select>
                    )}

                    {notifForm.target_type === 'specific' && (
                      <div className="space-y-2">
                        {/* Search Input */}
                        <input
                          type="text"
                          placeholder="Search users by name or email..."
                          value={notifForm.target_user_search || ''}
                          onChange={(e) => setNotifForm({...notifForm, target_user_search: e.target.value})}
                          className="w-full rounded-lg px-3 py-2 text-sm focus:outline-none"
                          style={{
                            backgroundColor: theme.background_primary,
                            border: `1px solid ${theme.border_color}`,
                            color: theme.text_primary
                          }}
                        />
                        {/* User List */}
                        <div 
                          className="max-h-40 overflow-y-auto space-y-1 rounded-lg p-2"
                          style={{ backgroundColor: theme.background_primary }}
                        >
                          {clientUsers.length === 0 ? (
                            <p className="text-xs text-center py-2" style={{ color: `${theme.text_primary}80` }}>Loading users...</p>
                          ) : (
                            clientUsers
                              .filter(user => {
                                if (!notifForm.target_user_search) return true;
                                const search = notifForm.target_user_search.toLowerCase();
                                return (
                                  user.email?.toLowerCase().includes(search) ||
                                  user.first_name?.toLowerCase().includes(search) ||
                                  user.name?.toLowerCase().includes(search)
                                );
                              })
                              .map((user) => (
                                <label 
                                  key={user.user_id || user.license_user_id} 
                                  className="flex items-center gap-2 p-2 rounded cursor-pointer hover:opacity-80"
                                >
                                  <input
                                    type="checkbox"
                                    checked={notifForm.target_user_ids.includes(user.user_id || user.license_user_id)}
                                    onChange={(e) => {
                                      const userId = user.user_id || user.license_user_id;
                                      const ids = e.target.checked
                                        ? [...notifForm.target_user_ids, userId]
                                        : notifForm.target_user_ids.filter(id => id !== userId);
                                      setNotifForm({...notifForm, target_user_ids: ids});
                                    }}
                                    className="w-4 h-4 rounded"
                                  />
                                  <span className="text-sm" style={{ color: theme.text_primary }}>{user.first_name || user.name || user.email}</span>
                                  <span className="text-xs" style={{ color: `${theme.text_primary}66` }}>{user.email}</span>
                                </label>
                              ))
                          )}
                          {notifForm.target_user_search && clientUsers.filter(user => {
                            const search = notifForm.target_user_search.toLowerCase();
                            return (
                              user.email?.toLowerCase().includes(search) ||
                              user.first_name?.toLowerCase().includes(search) ||
                              user.name?.toLowerCase().includes(search)
                            );
                          }).length === 0 && (
                            <p className="text-xs text-center py-2" style={{ color: `${theme.text_primary}80` }}>No users match your search</p>
                          )}
                        </div>
                      </div>
                    )}
                    {notifForm.target_type === 'specific' && notifForm.target_user_ids.length > 0 && (
                      <p className="text-xs" style={{ color: theme.accent_color }}>{notifForm.target_user_ids.length} user(s) selected</p>
                    )}
                  </div>
                </div>

                {/* Scheduling Section - Boxed style matching AdminDashboard */}
                <div 
                  className="rounded-lg p-4"
                  style={{ 
                    backgroundColor: `${theme.border_color}33`,
                    border: `1px solid ${theme.border_color}80`
                  }}
                >
                  <h4 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: theme.text_primary }}>
                    <Clock className="w-4 h-4" style={{ color: theme.accent_color }} />
                    Scheduling & Expiration
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs mb-1" style={{ color: `${theme.text_primary}99` }}>Schedule for (optional)</label>
                      <input
                        type="datetime-local"
                        value={notifForm.scheduled_at ? notifForm.scheduled_at.slice(0, 16) : ''}
                        onChange={(e) => setNotifForm({...notifForm, scheduled_at: e.target.value ? new Date(e.target.value).toISOString() : ''})}
                        className="w-full rounded-md px-3 py-2 text-sm"
                        style={{
                          backgroundColor: `${theme.border_color}4D`,
                          border: `1px solid ${theme.border_color}`,
                          color: theme.text_primary
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-xs mb-1" style={{ color: `${theme.text_primary}99` }}>Expires on (optional)</label>
                      <input
                        type="datetime-local"
                        value={notifForm.expires_at ? notifForm.expires_at.slice(0, 16) : ''}
                        onChange={(e) => setNotifForm({...notifForm, expires_at: e.target.value ? new Date(e.target.value).toISOString() : ''})}
                        className="w-full rounded-md px-3 py-2 text-sm"
                        style={{
                          backgroundColor: `${theme.border_color}4D`,
                          border: `1px solid ${theme.border_color}`,
                          color: theme.text_primary
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Action Buttons Section - Boxed style matching AdminDashboard */}
                <div 
                  className="rounded-lg p-4"
                  style={{ 
                    backgroundColor: `${theme.border_color}33`,
                    border: `1px solid ${theme.border_color}80`
                  }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold flex items-center gap-2" style={{ color: theme.text_primary }}>
                      <MousePointer className="w-4 h-4" style={{ color: theme.primary_color }} />
                      Action Buttons (optional)
                    </h4>
                    {notifForm.action_buttons.length < 2 && (
                      <button
                        type="button"
                        onClick={() => {
                          setNotifForm({
                            ...notifForm,
                            action_buttons: [...notifForm.action_buttons, { text: '', url: '', style: 'primary' }]
                          });
                        }}
                        className="text-xs hover:underline"
                        style={{ color: theme.accent_color }}
                      >
                        + Add Button
                      </button>
                    )}
                  </div>
                  {notifForm.action_buttons.length === 0 ? (
                    <p className="text-xs" style={{ color: `${theme.text_primary}80` }}>No action buttons. Users will see a "Got it!" dismiss button.</p>
                  ) : (
                    <div className="space-y-3">
                      {notifForm.action_buttons.map((btn, idx) => (
                        <div 
                          key={`action-btn-form-${idx}`} 
                          className="flex items-center gap-2 rounded-lg p-3"
                          style={{ backgroundColor: theme.background_primary }}
                        >
                          <div className="flex-1 grid grid-cols-3 gap-2">
                            <Input
                              value={btn.text}
                              onChange={(e) => {
                                const newButtons = [...notifForm.action_buttons];
                                newButtons[idx].text = e.target.value;
                                setNotifForm({ ...notifForm, action_buttons: newButtons });
                              }}
                              placeholder="Button text"
                              className="text-sm"
                              style={{
                                backgroundColor: `${theme.border_color}4D`,
                                borderColor: theme.border_color,
                                color: theme.text_primary
                              }}
                            />
                            <Input
                              value={btn.url}
                              onChange={(e) => {
                                const newButtons = [...notifForm.action_buttons];
                                newButtons[idx].url = e.target.value;
                                setNotifForm({ ...notifForm, action_buttons: newButtons });
                              }}
                              placeholder="URL (e.g., /journal)"
                              className="text-sm"
                              style={{
                                backgroundColor: `${theme.border_color}4D`,
                                borderColor: theme.border_color,
                                color: theme.text_primary
                              }}
                            />
                            <select
                              value={btn.style || 'primary'}
                              onChange={(e) => {
                                const newButtons = [...notifForm.action_buttons];
                                newButtons[idx].style = e.target.value;
                                setNotifForm({ ...notifForm, action_buttons: newButtons });
                              }}
                              className="rounded-md px-2 text-sm"
                              style={{
                                backgroundColor: `${theme.border_color}4D`,
                                border: `1px solid ${theme.border_color}`,
                                color: theme.text_primary
                              }}
                            >
                              <option value="primary">Primary</option>
                              <option value="secondary">Secondary</option>
                            </select>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const newButtons = [...notifForm.action_buttons];
                              newButtons.splice(idx, 1);
                              setNotifForm({ ...notifForm, action_buttons: newButtons });
                            }}
                            className="p-1 hover:opacity-70"
                            style={{ color: theme.error_color }}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Submit Buttons - Same layout as AdminDashboard */}
                <div className="flex gap-3 pt-2">
                  <Button
                    onClick={() => setShowNotifModal(false)}
                    variant="outline"
                    className="flex-1"
                    style={{
                      borderColor: theme.border_color,
                      color: `${theme.text_primary}B3`
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveNotif}
                    className="flex-1"
                    style={{
                      background: `linear-gradient(to right, ${theme.primary_color}, ${theme.accent_color})`,
                      color: theme.text_on_brand || '#ffffff'
                    }}
                  >
                    <Send className="w-4 h-4 mr-1" />
                    {notifForm.scheduled_at ? 'Schedule' : 'Send Now'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Banner Modal */}
        {showBannerModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div 
              className="rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
              style={{
                background: theme.background_secondary, // SOLID color,
                border: `1px solid ${theme.border_color}`
              }}
            >
              <div className="p-6" style={{ borderBottom: `1px solid ${theme.border_color}` }}>
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold" style={{ color: theme.text_primary }}>
                    {editingBanner ? 'Edit Banner' : 'Create Banner'}
                  </h3>
                  <button 
                    onClick={() => setShowBannerModal(false)} 
                    style={{ color: theme.text_secondary }}
                    className="hover:opacity-100"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <div className="p-6 space-y-4">
                {/* Title */}
                <div>
                  <label className="block text-sm mb-1" style={{ color: theme.text_secondary }}>Title *</label>
                  <Input
                    value={bannerForm.title}
                    onChange={(e) => setBannerForm({ ...bannerForm, title: e.target.value })}
                    placeholder="Banner title"
                    style={{
                      backgroundColor: `${theme.background_tertiary}4D`,
                      borderColor: theme.border_color,
                      color: theme.text_primary
                    }}
                  />
                </div>

                {/* Subtitle */}
                <div>
                  <label className="block text-sm mb-1" style={{ color: theme.text_secondary }}>Subtitle</label>
                  <Input
                    value={bannerForm.subtitle}
                    onChange={(e) => setBannerForm({ ...bannerForm, subtitle: e.target.value })}
                    placeholder="Optional subtitle"
                    style={{
                      backgroundColor: `${theme.background_tertiary}4D`,
                      borderColor: theme.border_color,
                      color: theme.text_primary
                    }}
                  />
                </div>

                {/* Position */}
                <div>
                  <label className="block text-sm mb-1" style={{ color: theme.text_secondary }}>Position</label>
                  <select
                    value={bannerForm.position}
                    onChange={(e) => setBannerForm({ ...bannerForm, position: e.target.value })}
                    className="w-full rounded-md px-3 py-2"
                    style={{
                      backgroundColor: `${theme.background_tertiary}4D`,
                      borderColor: theme.border_color,
                      color: theme.text_primary,
                      border: `1px solid ${theme.border_color}`
                    }}
                  >
                    <option value="main">Main Banner</option>
                    <option value="sidebar">Sidebar</option>
                  </select>
                </div>

                {/* Image Upload */}
                <div>
                  <label className="block text-sm mb-1" style={{ color: theme.text_secondary }}>Banner Image</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleBannerImageUpload}
                      className="hidden"
                      id="banner-image-upload"
                    />
                    <label
                      htmlFor="banner-image-upload"
                      className="flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer transition-all hover:opacity-80"
                      style={{
                        backgroundColor: `${theme.background_tertiary}4D`,
                        border: `1px solid ${theme.border_color}`,
                        color: theme.text_secondary
                      }}
                    >
                      {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      Upload Image
                    </label>
                    {bannerForm.image_url && (
                      <img src={bannerForm.image_url} alt="Preview" className="w-16 h-10 rounded object-cover" />
                    )}
                  </div>
                </div>

                {/* Gradient Colors (if no image) */}
                {!bannerForm.image_url && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm mb-1" style={{ color: theme.text_secondary }}>Gradient From</label>
                      <div className="flex items-center gap-2">
                        <input 
                          type="color" 
                          value={bannerForm.gradient_from}
                          onChange={(e) => setBannerForm({...bannerForm, gradient_from: e.target.value})}
                          className="w-10 h-8 rounded cursor-pointer border-0"
                        />
                        <Input
                          value={bannerForm.gradient_from}
                          onChange={(e) => setBannerForm({...bannerForm, gradient_from: e.target.value})}
                          className="flex-1"
                          style={{
                            backgroundColor: `${theme.background_tertiary}4D`,
                            borderColor: theme.border_color,
                            color: theme.text_primary
                          }}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm mb-1" style={{ color: theme.text_secondary }}>Gradient To</label>
                      <div className="flex items-center gap-2">
                        <input 
                          type="color" 
                          value={bannerForm.gradient_to}
                          onChange={(e) => setBannerForm({...bannerForm, gradient_to: e.target.value})}
                          className="w-10 h-8 rounded cursor-pointer border-0"
                        />
                        <Input
                          value={bannerForm.gradient_to}
                          onChange={(e) => setBannerForm({...bannerForm, gradient_to: e.target.value})}
                          className="flex-1"
                          style={{
                            backgroundColor: `${theme.background_tertiary}4D`,
                            borderColor: theme.border_color,
                            color: theme.text_primary
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* CTA */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm mb-1" style={{ color: theme.text_secondary }}>CTA Text</label>
                    <Input
                      value={bannerForm.cta_text}
                      onChange={(e) => setBannerForm({ ...bannerForm, cta_text: e.target.value })}
                      placeholder="Learn More"
                      style={{
                        backgroundColor: `${theme.background_tertiary}4D`,
                        borderColor: theme.border_color,
                        color: theme.text_primary
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-1" style={{ color: theme.text_secondary }}>CTA Link</label>
                    <Input
                      value={bannerForm.cta_link}
                      onChange={(e) => setBannerForm({ ...bannerForm, cta_link: e.target.value })}
                      placeholder="https://..."
                      style={{
                        backgroundColor: `${theme.background_tertiary}4D`,
                        borderColor: theme.border_color,
                        color: theme.text_primary
                      }}
                    />
                  </div>
                </div>

                {/* Preview */}
                <div>
                  <label className="block text-sm mb-2" style={{ color: theme.text_secondary }}>Preview</label>
                  <div 
                    className="rounded-lg p-4 flex items-center justify-between"
                    style={{
                      background: bannerForm.image_url 
                        ? `url(${bannerForm.image_url}) center/cover`
                        : (bannerForm.gradient_from || theme.primary_color), // SOLID color
                      minHeight: '80px'
                    }}
                  >
                    <div>
                      <p className="font-bold text-white drop-shadow">{bannerForm.title || 'Banner Title'}</p>
                      {bannerForm.subtitle && (
                        <p className="text-sm text-white/90 drop-shadow">{bannerForm.subtitle}</p>
                      )}
                    </div>
                    {bannerForm.cta_text && (
                      <span className="px-3 py-1.5 bg-white/20 backdrop-blur rounded-full text-sm text-white font-medium">
                        {bannerForm.cta_text}
                      </span>
                    )}
                  </div>
                </div>

                {/* Active Toggle */}
                <div 
                  className="flex items-center justify-between p-3 rounded-lg cursor-pointer"
                  style={{ backgroundColor: `${theme.background_tertiary}4D` }}
                  onClick={() => setBannerForm({ ...bannerForm, is_active: !bannerForm.is_active })}
                >
                  <span style={{ color: theme.text_primary }}>Activate Banner</span>
                  {bannerForm.is_active ? (
                    <ToggleRight className="w-6 h-6" style={{ color: theme.success_color }} />
                  ) : (
                    <ToggleLeft className="w-6 h-6" style={{ color: theme.text_secondary }} />
                  )}
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowBannerModal(false)}
                    style={{
                      borderColor: theme.border_color,
                      color: theme.text_secondary
                    }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleSaveBanner}
                    style={{
                      background: theme.primary_color,
                      color: theme.text_on_brand || '#ffffff'
                    }}
                  >
                    {editingBanner ? 'Update' : 'Create'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Upgrade Modal - Opens when clicking locked features */}
        {showUpgradeModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div 
              className="rounded-xl w-full max-w-md overflow-hidden"
              style={{
                background: theme.background_secondary, // SOLID color,
                border: `1px solid ${theme.border_color}`
              }}
            >
              {/* Header with gradient */}
              <div 
                className="p-6 text-center"
                style={{
                  background: `${theme.primary_color}40`
                }}
              >
                <Crown className="w-16 h-16 mx-auto mb-4" style={{ color: theme.warning_color }} />
                <h3 className="text-2xl font-bold mb-2" style={{ color: theme.text_primary }}>
                  Need More Features?
                </h3>
                <p className="text-sm" style={{ color: `${theme.text_secondary}CC` }}>
                  This feature is not included in your current plan.
                </p>
              </div>
              
              {/* Body */}
              <div className="p-6">
                <p className="text-center mb-6" style={{ color: theme.text_secondary }}>
                  Contact your account administrator to upgrade your plan and unlock additional features for your organization.
                </p>
                
                <div className="space-y-3">
                  <button 
                    onClick={() => setShowUpgradeModal(false)}
                    className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-medium transition-all hover:opacity-90"
                    style={{
                      background: theme.primary_color,
                      color: theme.text_on_brand || '#ffffff'
                    }}
                  >
                    <Send className="w-5 h-5" />
                    Got It
                  </button>
                  
                  <button 
                    onClick={() => setShowUpgradeModal(false)}
                    className="w-full py-3 rounded-xl font-medium transition-all hover:opacity-80"
                    style={{
                      backgroundColor: theme.background_tertiary,
                      color: theme.text_secondary,
                      border: `1px solid ${theme.border_color}`
                    }}
                  >
                    Maybe Later
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <footer className="mt-12 pt-6" style={{ borderTop: `1px solid ${theme.border_color}30` }}>
          <div className="flex flex-col items-center gap-2">
            {dashboard?.client?.features?.show_powered_by !== false && (
              <>
                <p className="text-xs" style={{ color: `${theme.text_secondary}60` }}>
                  Powered by
                </p>
                <p className="text-sm font-semibold" style={{ color: `${theme.text_secondary}90` }}>
                  UnCloned® Journal
                </p>
              </>
            )}
            <p className="text-xs" style={{ color: `${theme.text_secondary}40` }}>
              © {new Date().getFullYear()} {dashboard?.client?.name || 'All rights reserved'}
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
}
